import { useState, useEffect, useMemo, useRef, useCallback, lazy, Suspense } from 'react'
import { useParams } from 'react-router-dom'
import { AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart } from 'recharts'
import { Loader, X, ChevronDown } from 'lucide-react'
import { projectsApi } from '../../lib/projectsApi'
import { kbApi } from '../../lib/kbApi'
import { useDashboardContext } from '../../context/DashboardContext'
import useAuthStore from '../../store/auth-store'
import { resolveIanaTz, wallClockPartsInTz, parseWallClockInTzToMs } from '../../lib/tz'
import { DataTableModal, StatisticsModal } from './LineChartDataModals'
import WindRoseChart from './WindRoseChart'
import { EventMonitorPanel } from '../EventMonitorPanel'
import { alarmApi } from '../../lib/alarmApi'
import { GalleryEditorModal, SupportProjectEditorModal } from './GalleryAndSupportEditors'
// Project Map renderer (Leaflet + free tile layers) and its Edit popup —
// powers the GPS widget on the Project Map dashboard.
import ProjectMapView from './map/ProjectMapView'

export const COLORS = ['#358ECD', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899']

// Wind Rose speed band definitions (label + default colour). Default
// palette matches the XD/PSD swatches from the client: light-blue → indigo
// → green → orange → slate → magenta → teal.
const WIND_SPEED_BANDS = [
  { label: '< 0.5 m/s',  key: 'band0', defaultColor: '#7DD3F0' },
  { label: '0.5–2 m/s',  key: 'band1', defaultColor: '#5B5DD8' },
  { label: '2–4 m/s',    key: 'band2', defaultColor: '#56B565' },
  { label: '4–6 m/s',    key: 'band3', defaultColor: '#F37D5C' },
  { label: '6–8 m/s',    key: 'band4', defaultColor: '#7C8A9C' },
  { label: '8–10 m/s',   key: 'band5', defaultColor: '#D67BDB' },
  { label: '> 10 m/s',   key: 'band6', defaultColor: '#5EEAD4' },
]

// ─── Wind Rose: known Parameterlib IDs for wind speed / direction ─────────────
// Source: client-supplied list. Paired by index — WIND_PAIRS[i] is the
// (speed, direction) PID tuple for variant i, in client-priority order.
//
//   Wind Speed/Direction:                47/48
//   Avg Wind Sp/Dir:                     49/50
//   Min Wind Sp/Dir:                     51/52
//   Max Wind Sp/Dir:                     53/54
//   Wind Gust/Dir:                      195/196
//   Relative Wind Speed/Dir:           1586/1587
//   Corrected Wind Speed/Dir:          1588/1589
//   WMO Avg Wind Speed/Dir:            1590/1591
//   WMO Avg Corrected Wind Speed/Dir:  1592/1593
//   WMO Relative Wind Gust/Dir:        1594/1616
//   Vct Wind Speed/Dir:                1664/1665
//   Std Wind Speed/Dir:                1667/1668
//
const WIND_PAIRS = [
  [47, 48], [49, 50], [51, 52], [53, 54], [195, 196],
  [1586, 1587], [1588, 1589], [1590, 1591], [1592, 1593], [1594, 1616],
  [1664, 1665], [1667, 1668],
]
const WIND_SPEED_PIDS = new Set(WIND_PAIRS.map(([s]) => s))
const WIND_DIR_PIDS   = new Set(WIND_PAIRS.map(([, d]) => d))

// Flatten every parameter on a device (across all its sensors) into a
// single list. fetchProjectSites returns devices as `{sensors:[{parameters:[]}, ...]}`
// — the earlier Wind Rose dropdowns read `dev.parameters` which is always
// undefined and silently shows an empty list.
function deviceFlatParams(device) {
  if (!device) return []
  if (Array.isArray(device.parameters) && device.parameters.length) return device.parameters
  const out = []
  ;(device.sensors || []).forEach((sensor) => {
    ;(sensor.parameters || []).forEach((p) => {
      out.push({ ...p, sensorName: sensor.name, sensorId: sensor.id })
    })
  })
  return out
}

// Given a project's sites tree, find the first (in site → device order)
// device that has BOTH a wind-speed param and a wind-direction param
// based on the PID lists above. Returns `{ deviceId, speedParamId,
// directionParamId }` or null when no qualifying device exists.
function detectWindDefaults(sites) {
  if (!Array.isArray(sites)) return null
  for (const site of sites) {
    for (const dev of site.devices || []) {
      const params = deviceFlatParams(dev)
      // Walk WIND_PAIRS in their preferred order so "Wind Speed/Direction
      // (47/48)" beats "Avg Wind Sp/Dir (49/50)" when a device exposes
      // both pairs.
      for (const [speedPid, dirPid] of WIND_PAIRS) {
        const speed = params.find((p) => p.parameter_id === speedPid)
        const dir   = params.find((p) => p.parameter_id === dirPid)
        if (speed && dir) {
          return { deviceId: dev.id, speedParamId: speed.id, directionParamId: dir.id }
        }
      }
    }
  }
  return null
}

// ─── localStorage helpers ──────────────────────────────────────────────────────
const lsGet = (key, fallback = null) => {
  try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : fallback } catch { return fallback }
}
const lsSet = (key, value) => {
  try { localStorage.setItem(key, JSON.stringify(value)); return true } catch { return false }
}

// Build a project-scoped widget-settings key. Configurable widgets (Gallery,
// Support Our Project, Wind Rose, Line Chart, Project Photo) previously
// keyed their localStorage by `widget.id` ONLY — but template widget ids
// are template-wide, so two projects using the same Overview template
// would collide on the same key and one project's settings leaked into
// every other project. Scope by projectId to fix that.
//
// An earlier draft of this fix transparently copied legacy un-scoped
// values into the new scoped key on first read. That re-introduced the
// leak — whichever project loaded first cloned the leaked value into
// its own scope. Drop legacy keys outright instead, see the IIFE below.
// Affected users reconfigure once (typically just one project per widget).
const scopedLsKey = (prefix, projectId, widgetId) =>
  `${prefix}_p${projectId || 'noproject'}_w${widgetId}`
const lsGetScoped = (prefix, projectId, widgetId, fallback = null) =>
  lsGet(scopedLsKey(prefix, projectId, widgetId), fallback)
const lsSetScoped = (prefix, projectId, widgetId, value) =>
  lsSet(scopedLsKey(prefix, projectId, widgetId), value)

// One-time module-level cleanup: any legacy un-scoped widget-settings
// key (`wq_gallery_<id>`, `wq_support_<id>`, …) is removed so the cross-
// project leak can't survive across reloads. Legacy keys are pre-bug —
// there's no way to know which project originally wrote them, so we
// drop them rather than migrate. The regex matches `<prefix>_<digits>`
// only; the new project-scoped shape contains `_p<id>_w<id>` and is
// preserved.
;(function purgeLegacyWidgetSettings() {
  try {
    const legacyShape = /^(wq_gallery|wq_support|wq_windrose|wq_graph|wq_photo)_\d+$/
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i)
      if (k && legacyShape.test(k)) {
        try { localStorage.removeItem(k) } catch (_) { /* ignore */ }
      }
    }
  } catch (_) { /* localStorage may be disabled */ }
})()

// Shared edit/view icon used in XD headers
const EditIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M2.22222 17.7778H3.80556L14.6667 6.91663L13.0833 5.33337L2.22222 16.1945V17.7778ZM0 20V15.2778L14.6667 0.638916C14.8889 0.435181 15.1342 0.277832 15.4028 0.166687C15.6713 0.055542 15.9537 0 16.25 0C16.5463 0 16.8333 0.055542 17.1111 0.166687C17.3889 0.277832 17.6296 0.444458 17.8333 0.666626L19.3611 2.22223C19.5833 2.4259 19.7454 2.66669 19.8472 2.94446C19.9491 3.22223 20 3.5 20 3.77777C20 4.0741 19.9491 4.35651 19.8472 4.625C19.7454 4.89355 19.5833 5.13892 19.3611 5.36108L4.72221 20H0ZM13.8611 6.13892L13.0833 5.33337L14.6667 6.91663L13.8611 6.13892Z" fill="#888888"/>
  </svg>
)
const ExternalLinkIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M2.22222 20C1.61111 20 1.08797 19.7824 0.652779 19.3472C0.21759 18.912 0 18.3889 0 17.7778V2.22223C0 1.61108 0.21759 1.08795 0.652779 0.652771C1.08797 0.21759 1.61111 0 2.22222 0H10V2.22223H2.22222V17.7778H17.7778V10H20V17.7778C20 18.3889 19.7824 18.912 19.3472 19.3472C18.912 19.7824 18.3889 20 17.7778 20H2.22222ZM7.44445 14.1111L5.88889 12.5555L16.2222 2.22223H12.2222V0H20V7.77777H17.7778V3.77777L7.44445 14.1111Z" fill="#888888"/>
  </svg>
)

// XD-matching card: full-height, header + content
function XDCard({ title, subtitle, headerRight, children, className = '' }) {
  return (
    <div className={`bg-white rounded-lg shadow-sm h-full flex flex-col overflow-hidden ${className}`}>
      <div className="flex border-b border-gray-100 items-center justify-between px-4 py-3 shrink-0">
        <div>
          <h3 className="font-bold text-[#4D4D4D] text-base">{title}</h3>
          {subtitle && <p className="text-xs text-gray-400 mt-0.5 leading-tight">{subtitle}</p>}
        </div>
        {headerRight}
      </div>
      <div className="flex-1 overflow-auto">
        {children}
      </div>
    </div>
  )
}

// Legacy card (kept for non-XD widgets)
function Card({ children, className = '' }) {
  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-100 h-full ${className}`}>
      {children}
    </div>
  )
}

/**
 * Summary Card Widget - displays a single metric
 */
export function SummaryCard({ widget, data }) {
  return (
    <Card className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
      <h3 className="text-sm font-semibold text-gray-700 mb-2">{widget.title}</h3>
      <div className="text-3xl font-bold text-blue-600">{data?.value || '—'}</div>
      {data?.change && (
        <p className="text-xs text-gray-600 mt-2">
          {data.change > 0 ? '↑' : '↓'} {Math.abs(data.change)}% from last period
        </p>
      )}
    </Card>
  )
}

// ─── Point shape dot renderers ────────────────────────────────────────────────
export const POINT_SHAPES = ['Circle', 'Square', 'Diamond', 'Triangle', 'None']

// Small colour swatch drawn in the selected point shape (Graph Editor).
function ShapeSwatch({ shape, color }) {
  const c = color || '#358ECD'
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden>
      {shape === 'Square' && <rect x="2" y="2" width="12" height="12" fill={c} />}
      {shape === 'Diamond' && <polygon points="8,1 15,8 8,15 1,8" fill={c} />}
      {shape === 'Triangle' && <polygon points="8,1 15,14 1,14" fill={c} />}
      {shape === 'None' && <circle cx="8" cy="8" r="6" fill="none" stroke={c} strokeWidth="2" />}
      {(!shape || shape === 'Circle') && <circle cx="8" cy="8" r="6" fill={c} />}
    </svg>
  )
}

function makeDot(shape, color) {
  if (shape === 'None') return false
  if (shape === 'Square') return (props) => {
    const { cx, cy } = props
    const s = 5
    return <rect x={cx - s / 2} y={cy - s / 2} width={s} height={s} fill={color} />
  }
  if (shape === 'Diamond') return (props) => {
    const { cx, cy } = props
    const r = 4
    return <polygon points={`${cx},${cy - r} ${cx + r},${cy} ${cx},${cy + r} ${cx - r},${cy}`} fill={color} />
  }
  if (shape === 'Triangle') return (props) => {
    const { cx, cy } = props
    const r = 5
    return <polygon points={`${cx},${cy - r} ${cx + r},${cy + r} ${cx - r},${cy + r}`} fill={color} />
  }
  // default Circle
  return { r: 3, fill: color, strokeWidth: 0 }
}

// ─── Graph Editor Modal ────────────────────────────────────────────────────────
export function GraphEditorModal({ open, onClose, onSave, selectedParameters, seriesMetadata, settings, widget }) {
  const [draft, setDraft] = useState(() => ({ ...settings }))
  useEffect(() => { if (open) setDraft({ ...settings }) }, [open, settings])

  if (!open) return null

  const updateSeries = (paramId, field, value) => {
    setDraft(prev => ({
      ...prev,
      series: {
        ...prev.series,
        [paramId]: { ...(prev.series?.[paramId] || {}), [field]: value },
      },
    }))
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">
        {/* Modal header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-[#4D4D4D]">{selectedParameters.length > 1 ? 'Multichart Editor' : 'Line Chart Editor'}</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { onSave(draft); onClose() }}
              className="px-4 py-1.5 bg-[#358ECD] text-white text-sm font-medium rounded hover:bg-[#2d7ab8] transition"
            >Save</button>
            <button
              onClick={onClose}
              className="px-4 py-1.5 bg-white border border-gray-200 text-gray-600 text-sm rounded hover:bg-gray-50 transition"
            >Cancel</button>
          </div>
        </div>

        {/* Modal body — one block per parameter: colour, point shape + its own
            vertical axis (Min/Max/Steps). No shared sections / "set as default". */}
        <div className="overflow-y-auto flex-1 px-6 py-5">
          {selectedParameters.length === 0 ? (
            <p className="text-xs text-gray-400">Select parameters from the left panel first.</p>
          ) : selectedParameters.map((param, idx) => {
            const meta = seriesMetadata[param.id] || {}
            const sLabel = [meta.siteName, meta.name || param.name].filter(Boolean).join(' | ')
            const s = draft.series?.[param.id] || {}
            const lineColor = s.color || COLORS[idx % COLORS.length]
            const pointShape = s.pointShape || 'Circle'
            const numOrUndef = (v) => (v === '' ? undefined : Number(v))
            return (
              <div key={param.id} className="mb-5 pb-5 border-b border-gray-100 last:border-0 last:mb-0 last:pb-0">
                <p className="text-sm font-semibold text-[#4D4D4D] mb-3">{sLabel}</p>
                <div className="flex items-center gap-6 flex-wrap mb-3">
                  {/* Line Color — swatch drawn in the selected point shape + picker */}
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-500 font-medium">Line Color</label>
                    <label className="flex items-center gap-1 border border-gray-200 rounded px-1.5 py-1 cursor-pointer hover:bg-gray-50" title={lineColor}>
                      <ShapeSwatch shape={pointShape} color={lineColor} />
                      <ChevronDown className="w-3 h-3 text-gray-400" />
                      <input
                        type="color"
                        value={lineColor}
                        onChange={e => updateSeries(param.id, 'color', e.target.value)}
                        className="sr-only"
                      />
                    </label>
                  </div>
                  {/* Point Shape */}
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-500 font-medium">Point Shape</label>
                    <select
                      value={pointShape}
                      onChange={e => updateSeries(param.id, 'pointShape', e.target.value)}
                      className="text-xs border border-gray-200 rounded px-2 py-1 focus:outline-none focus:border-[#358ECD]"
                    >
                      {POINT_SHAPES.map(sh => <option key={sh}>{sh}</option>)}
                    </select>
                  </div>
                </div>
                {/* Per-parameter vertical axis */}
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-1.5">
                    <label className="text-xs text-gray-500 font-medium">Min</label>
                    <input type="number" placeholder="Minimum #" value={s.yMin ?? ''} onChange={e => updateSeries(param.id, 'yMin', numOrUndef(e.target.value))} className="w-24 text-xs border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:border-[#358ECD]" />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <label className="text-xs text-gray-500 font-medium">Max</label>
                    <input type="number" placeholder="Maximum #" value={s.yMax ?? ''} onChange={e => updateSeries(param.id, 'yMax', numOrUndef(e.target.value))} className="w-24 text-xs border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:border-[#358ECD]" />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <label className="text-xs text-gray-500 font-medium">Steps</label>
                    <input type="number" placeholder="Default" value={s.ySteps ?? ''} onChange={e => updateSeries(param.id, 'ySteps', numOrUndef(e.target.value))} className="w-20 text-xs border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:border-[#358ECD]" />
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// ─── Wind Rose Editor Modal ────────────────────────────────────────────────────
function WindRoseEditorModal({ open, onClose, onSave, projectId, initialSettings = {} }) {
  const defaultColors = Object.fromEntries(WIND_SPEED_BANDS.map(b => [b.key, b.defaultColor]))

  const [widgetName, setWidgetName] = useState(initialSettings.widgetName || '')
  const [sites, setSites] = useState([])
  const [selectedDevice, setSelectedDevice] = useState(initialSettings.selectedDevice || '')
  const [windDirectionParam, setWindDirectionParam] = useState(initialSettings.windDirectionParam || '')
  const [windSpeedParam, setWindSpeedParam] = useState(initialSettings.windSpeedParam || '')
  const [keyColors, setKeyColors] = useState(initialSettings.keyColors || defaultColors)
  const [loading, setLoading] = useState(false)

  // Re-populate form fields whenever modal opens (uses latest saved settings)
  useEffect(() => {
    if (open) {
      setWidgetName(initialSettings.widgetName || '')
      setSelectedDevice(initialSettings.selectedDevice || '')
      setWindDirectionParam(initialSettings.windDirectionParam || '')
      setWindSpeedParam(initialSettings.windSpeedParam || '')
      setKeyColors(initialSettings.keyColors || defaultColors)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Flatten all devices across sites
  const allDevices = useMemo(() => {
    const devs = []
    sites.forEach(site => {
      ;(site.devices || []).forEach(dev => {
        devs.push({ ...dev, siteName: site.name || site.station_name || '' })
      })
    })
    return devs
  }, [sites])

  // Parameters of selected device — flattened across all sensors so the
  // dropdown actually populates. fetchProjectSites returns parameters
  // under `dev.sensors[].parameters[]`, never `dev.parameters` directly.
  const deviceParams = useMemo(() => {
    if (!selectedDevice) return []
    const dev = allDevices.find(d => String(d.id) === String(selectedDevice))
    return deviceFlatParams(dev)
  }, [selectedDevice, allDevices])

  // Fetch project sites when modal opens
  useEffect(() => {
    if (!open || !projectId) return
    setLoading(true)
    projectsApi.fetchProjectSites(Number(projectId))
      .then(data => setSites(Array.isArray(data) ? data : []))
      .catch(() => setSites([]))
      .finally(() => setLoading(false))
  }, [open, projectId])

  // Auto-default: when the modal opens with no saved selection AND we've
  // just loaded the sites tree, scan for the first device that has both
  // a wind-speed and wind-direction parameter (per WIND_PAIRS) and
  // pre-select it. This matches the customer spec — "Parameter selection
  // defaults to wind speed and wind direction of the first logger which
  // has wind speed and wind direction parameters."
  useEffect(() => {
    if (!open) return
    if (selectedDevice || windDirectionParam || windSpeedParam) return
    if (!sites || sites.length === 0) return
    const defaults = detectWindDefaults(sites)
    if (!defaults) return
    setSelectedDevice(String(defaults.deviceId))
    setWindSpeedParam(String(defaults.speedParamId))
    setWindDirectionParam(String(defaults.directionParamId))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sites])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-[#4D4D4D]">Edit Wind Rose</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded transition">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
          {/* Widget Name */}
          <div>
            <label className="block text-xs font-semibold text-[#4D4D4D] mb-1">
              Widget Name
              <span className="ml-1 font-normal text-gray-400">(Leave Blank For Default)</span>
            </label>
            <input
              type="text"
              placeholder="Leave Blank For Default"
              value={widgetName}
              onChange={e => setWidgetName(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD]"
            />
          </div>

          {/* Parameter Selection */}
          <div>
            <h3 className="text-xs font-semibold text-[#4D4D4D] mb-3">Parameter Selection</h3>

            {/* Device */}
            <div className="mb-3">
              <label className="block text-xs text-gray-500 mb-1">Device</label>
              <select
                value={selectedDevice}
                onChange={e => {
                  setSelectedDevice(e.target.value)
                  setWindDirectionParam('')
                  setWindSpeedParam('')
                }}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD]"
              >
                <option value="">{loading ? 'Loading devices…' : 'Select Device…'}</option>
                {allDevices.map(dev => (
                  <option key={dev.id} value={dev.id}>
                    {dev.siteName ? `${dev.siteName} — ` : ''}{dev.name || dev.serial_number || `Device ${dev.id}`}
                  </option>
                ))}
              </select>
            </div>

            {/* Wind Direction */}
            <div className="mb-3">
              <label className="block text-xs text-gray-500 mb-1">Wind Direction</label>
              <select
                value={windDirectionParam}
                onChange={e => setWindDirectionParam(e.target.value)}
                disabled={!selectedDevice || deviceParams.length === 0}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD] disabled:bg-gray-50 disabled:text-gray-400"
              >
                <option value="">Select Parameter…</option>
                {deviceParams.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>

            {/* Wind Speed */}
            <div>
              <label className="block text-xs text-gray-500 mb-1">Wind Speed</label>
              <select
                value={windSpeedParam}
                onChange={e => setWindSpeedParam(e.target.value)}
                disabled={!selectedDevice || deviceParams.length === 0}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD] disabled:bg-gray-50 disabled:text-gray-400"
              >
                <option value="">Select Parameter…</option>
                {deviceParams.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Key Colors */}
          <div>
            <h3 className="text-xs font-semibold text-[#4D4D4D] mb-3">Key Colors</h3>
            <div className="space-y-2.5">
              {WIND_SPEED_BANDS.map(band => (
                <div key={band.key} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 w-24">{band.label}</span>
                  <div className="flex items-center gap-2">
                    <div
                      className="w-6 h-6 rounded border border-gray-300"
                      style={{ backgroundColor: keyColors[band.key] }}
                    />
                    <input
                      type="color"
                      value={keyColors[band.key]}
                      onChange={e => setKeyColors(prev => ({ ...prev, [band.key]: e.target.value }))}
                      className="w-8 h-8 rounded border border-gray-200 cursor-pointer p-0.5"
                      title={`Pick color for ${band.label}`}
                    />
                    <span className="text-xs text-gray-400 font-mono w-16">{keyColors[band.key].toUpperCase()}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
          >Cancel</button>
          <button
            onClick={() => {
              onSave({ widgetName, selectedDevice, windDirectionParam, windSpeedParam, keyColors })
              onClose()
            }}
            className="px-5 py-2 text-sm text-white bg-[#358ECD] rounded-lg hover:bg-[#2d7ab8] transition font-medium"
          >Save</button>
        </div>
      </div>
    </div>
  )
}

// ─── Change Project Photo Modal ────────────────────────────────────────────────
function ChangeProjectPhotoModal({ open, onClose, onSave, currentUrl }) {
  const [dragOver, setDragOver] = useState(false)
  const [file, setFile] = useState(null)
  const [previewUrl, setPreviewUrl] = useState('')
  const [zoom, setZoom] = useState(1)
  const [saving, setSaving] = useState(false)
  const fileInputRef = useRef(null)

  useEffect(() => {
    if (open) {
      setFile(null)
      setPreviewUrl(currentUrl || '')
      setZoom(1)
      setDragOver(false)
    }
  }, [open, currentUrl])

  const handleFilePick = (f) => {
    if (!f || !f.type.startsWith('image/')) return
    setFile(f)
    const reader = new FileReader()
    reader.onload = e => setPreviewUrl(e.target.result)
    reader.readAsDataURL(f)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragOver(false)
    handleFilePick(e.dataTransfer.files[0])
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      await onSave(file, zoom)
    } finally {
      setSaving(false)
      onClose()
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-[#4D4D4D]">Change Project Photo</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded transition">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Drag-and-drop upload zone */}
          <div
            role="button"
            tabIndex={0}
            className={`border-2 border-dashed rounded-xl flex flex-col items-center justify-center py-10 px-6 cursor-pointer transition select-none ${
              dragOver ? 'border-[#358ECD] bg-blue-50' : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
            }`}
            onDragOver={e => { e.preventDefault(); setDragOver(true) }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={e => e.key === 'Enter' && fileInputRef.current?.click()}
          >
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" className="text-gray-400 mb-3" aria-hidden>
              <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <polyline points="17,8 12,3 7,8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <line x1="12" y1="3" x2="12" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <p className="text-sm text-gray-500 text-center leading-relaxed">
              <span className="text-[#358ECD] font-medium">Drag Attachments</span> or{' '}
              <span className="text-[#358ECD] font-medium">Upload Featured Photo</span>
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={e => handleFilePick(e.target.files[0])}
            />
          </div>

          {/* Image crop preview + zoom slider */}
          {previewUrl && (
            <div className="space-y-2">
              <div className="w-full h-44 rounded-lg overflow-hidden border border-gray-200 bg-gray-100 flex items-center justify-center">
                <img
                  src={previewUrl}
                  alt="Preview"
                  draggable={false}
                  style={{
                    transform: `scale(${zoom})`,
                    transition: 'transform 0.15s ease',
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    cursor: 'grab',
                  }}
                />
              </div>
              <p className="text-xs text-gray-400 text-center">Drag to move image within the crop area.</p>

              {/* Zoom slider */}
              <div className="flex items-center gap-3 pt-1">
                <button
                  className="text-gray-500 hover:text-gray-800 text-xl font-bold leading-none pb-0.5 transition"
                  onClick={() => setZoom(z => Math.max(0.5, +(z - 0.1).toFixed(1)))}
                  aria-label="Zoom out"
                >−</button>
                <input
                  type="range"
                  min="0.5"
                  max="3"
                  step="0.1"
                  value={zoom}
                  onChange={e => setZoom(Number(e.target.value))}
                  className="flex-1 accent-[#358ECD]"
                />
                <button
                  className="text-gray-500 hover:text-gray-800 text-xl font-bold leading-none pb-0.5 transition"
                  onClick={() => setZoom(z => Math.min(3, +(z + 0.1).toFixed(1)))}
                  aria-label="Zoom in"
                >+</button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 space-y-3">
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full py-2.5 text-sm font-semibold text-white bg-[#358ECD] rounded-lg hover:bg-[#2d7ab8] transition disabled:opacity-60"
          >
            {saving ? 'Saving…' : 'Save Project Photo'}
          </button>
          <button
            onClick={onClose}
            className="w-full py-2 text-sm text-gray-500 hover:text-gray-700 transition"
          >Cancel</button>
        </div>
      </div>
    </div>
  )
}

/**
 * Line Chart Widget — connected to DashboardContext for real sensor data
 * XD design: D/W/M/Y + Start Date + End Date + three-dot menu + pencil editor
 */
export function LineChartWidget({ widget }) {
  const { projectId } = useParams()
  const {
    selectedParameters = [],
    dateRange = {},
    activePeriod = 'D',
    handlePeriodClick,
    setDateRange,
    isCustomizing = false,
  } = useDashboardContext()

  // Customer's configured timezone (from Account.timezone, populated by the
  // login response).  AccountModal stores display strings like
  // "(UTC-05:00) Eastern Time (US & Canada)" — resolveIanaTz() maps those
  // to IANA names that Intl will accept.  Defensive: never throws, falls
  // back to browser TZ then UTC if all else fails.
  const rawTz = useAuthStore(s => s.user?.timezone)
  const customerTz = useMemo(() => resolveIanaTz(rawTz), [rawTz])

  const [loading, setLoading] = useState(false)
  const [rawData, setRawData] = useState([])
  const [seriesMetadata, setSeriesMetadata] = useState({})
  const [dataWarning, setDataWarning] = useState(null)

  // Data Table + Statistics are now popups, not inline view-mode swaps.
  // The chart always renders inline behind the modal backdrop.
  const [tableModalOpen, setTableModalOpen] = useState(false)
  const [statsModalOpen, setStatsModalOpen] = useState(false)

  // Graph editor state — persisted to localStorage, PROJECT-SCOPED so
  // two projects sharing the Overview template don't collide on the
  // same widget.id key. See scopedLsKey doc-block at the top of file.
  const [showEditor, setShowEditor] = useState(false)
  const [graphSettings, setGraphSettings] = useState(() =>
    lsGetScoped('wq_graph', projectId, widget.id,
      { series: {}, yMin: undefined, yMax: undefined, ySteps: undefined })
  )

  // Three-dot menu + chart DOM ref (for PNG/PDF export)
  const [showMenu, setShowMenu] = useState(false)
  const menuRef = useRef(null)
  const chartContainerRef = useRef(null)
  useEffect(() => {
    if (!showMenu) return
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false) }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [showMenu])

  // Date input values (YYYY-MM-DD for <input type="date">)
  const fromDate = dateRange.from ? dateRange.from.split(' ')[0] : ''
  const toDate   = dateRange.to   ? dateRange.to.split(' ')[0]   : ''

  // D/W/M/Y: set activePeriod AND auto-set the dateRange window.
  // The window endpoints are computed in the CUSTOMER's timezone so the
  // chart shows a rolling [now − N, now] in their local wall-clock — matching
  // the legacy Symfony getDatesFromRangeV3() behaviour exactly.
  //   D : now − 24h        W : now − 7 days
  //   M : now − 30 days    Y : same calendar day/time, previous year
  const handlePeriodButtonClick = (p) => {
    handlePeriodClick?.(p)
    const nowInstant = new Date()
    // Wall-clock parts of "now" as seen in the customer's TZ.
    const nowParts = wallClockPartsInTz(nowInstant, customerTz)
    // Build a "shadow" Date in *browser-local* whose numeric components equal
    // those wall-clock parts.  This lets us use the native Date calendar
    // arithmetic (setDate / setFullYear) without pulling in date-fns-tz.
    const shadowNow = new Date(
      nowParts.year, nowParts.month - 1, nowParts.day,
      nowParts.hour, nowParts.minute, nowParts.second
    )
    const shadowStart = new Date(shadowNow)
    if (p === 'D')      shadowStart.setTime(shadowNow.getTime() - 24 * 60 * 60 * 1000)
    else if (p === 'W') shadowStart.setDate(shadowNow.getDate() - 7)
    else if (p === 'M') shadowStart.setDate(shadowNow.getDate() - 30)
    else if (p === 'Y') shadowStart.setFullYear(shadowNow.getFullYear() - 1)

    const pad = n => String(n).padStart(2, '0')
    const shadowFmt = d =>
      `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ` +
      `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`

    setDateRange?.({ from: shadowFmt(shadowStart), to: shadowFmt(shadowNow) })
  }

  const MAX_POINTS = 50000
  useEffect(() => {
    if (!selectedParameters.length) {
      setRawData([])
      setSeriesMetadata({})
      setDataWarning(null)
      return
    }
    let cancelled = false
    const fetchData = async () => {
      setLoading(true)
      setDataWarning(null)
      try {
        const allData = {}
        const metadata = {}
        for (const param of selectedParameters) {
          if (!param.id) continue
          try {
            const response = await projectsApi.fetchGraphData(Number(param.id), {
              fromDate: dateRange.from,   // wall-clock in customer TZ
              toDate: dateRange.to,       // wall-clock in customer TZ
              timezone: customerTz,       // tells backend how to interpret the strings
            })
            if (!response || response.error || !Array.isArray(response.data)) continue
            metadata[param.id] = {
              name: param.name,
              unit: response.unit || '',
              siteName: param.siteName || param.site_name || '',
              precision: response.precision ?? 0,
            }
            response.data.forEach(point => {
              if (point.x == null || point.y == null) return
              if (!allData[point.x]) allData[point.x] = { x: point.x }
              allData[point.x][`p_${param.id}`] = point.y
            })
          } catch { /* skip failed param */ }
        }
        if (cancelled) return
        let processed = Object.values(allData)
          .filter(d => Object.keys(d).some(k => k !== 'x' && d[k] != null))
          .sort((a, b) => a.x - b.x)
        // Large-dataset protection: truncate + warn
        if (processed.length > MAX_POINTS) {
          processed = processed.slice(-MAX_POINTS)
          setDataWarning(`Dataset too large — showing the most recent ${MAX_POINTS.toLocaleString()} records only.`)
        }
        setRawData(processed)
        setSeriesMetadata(metadata)
      } catch (err) {
        console.error('[LineChartWidget] fetch error', err)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    fetchData()
    return () => { cancelled = true }
  }, [selectedParameters, dateRange])

  // ── Aggregate helpers ───────────────────────────────────────────────────────
  const _aggregate = (buckets) =>
    Object.entries(buckets)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, v]) => {
        const result = { x: v.x }
        Object.keys(v._sums).forEach(k => {
          result[k] = v._counts[k] ? +(v._sums[k] / v._counts[k]).toFixed(4) : null
        })
        return result
      })

  // Aggregate raw timestamp data per activePeriod
  const graphData = useMemo(() => {
    // Helper: empty bucket for a label
    const emptyBucket = (label) => ({ x: label, _sums: {}, _counts: {} })

    // Y view → group by YYYY-MM, label "Jun 2025".
    // Pre-fill all 12 month slots so the axis always spans the full year,
    // even when data only exists for some months.
    if (activePeriod === 'Y') {
      const byMonth = {}
      // 1. Pre-fill all 12 months (oldest → newest)
      const now = new Date()
      for (let i = 11; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
        if (!byMonth[key]) {
          byMonth[key] = emptyBucket(
            d.toLocaleString('en-US', { month: 'short', year: 'numeric' })
          )
        }
      }
      // 2. Accumulate actual data into the buckets
      rawData.forEach(point => {
        const d = new Date(point.x)
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
        if (!byMonth[key]) {
          byMonth[key] = emptyBucket(
            d.toLocaleString('en-US', { month: 'short', year: 'numeric' })
          )
        }
        Object.keys(point).forEach(k => {
          if (k === 'x') return
          byMonth[key]._sums[k] = (byMonth[key]._sums[k] || 0) + point[k]
          byMonth[key]._counts[k] = (byMonth[key]._counts[k] || 0) + 1
        })
      })
      return _aggregate(byMonth)
    }

    // M view → group by YYYY-MM-DD, label "02 May".
    // Pre-fill all 30 days so the axis always covers the full month window.
    if (activePeriod === 'M') {
      const byDate = {}
      // 1. Pre-fill all 30 days (oldest → newest)
      const now = new Date()
      for (let i = 29; i >= 0; i--) {
        const d = new Date(now)
        d.setDate(d.getDate() - i)
        const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
        if (!byDate[key]) {
          byDate[key] = emptyBucket(
            `${String(d.getDate()).padStart(2, '0')} ${d.toLocaleString('en-US', { month: 'short' })}`
          )
        }
      }
      // 2. Accumulate actual data
      rawData.forEach(point => {
        const d = new Date(point.x)
        const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
        if (!byDate[key]) {
          byDate[key] = emptyBucket(
            `${String(d.getDate()).padStart(2, '0')} ${d.toLocaleString('en-US', { month: 'short' })}`
          )
        }
        Object.keys(point).forEach(k => {
          if (k === 'x') return
          byDate[key]._sums[k] = (byDate[key]._sums[k] || 0) + point[k]
          byDate[key]._counts[k] = (byDate[key]._counts[k] || 0) + 1
        })
      })
      return _aggregate(byDate)
    }

    // W view → group by YYYY-MM-DD, label "18 May".
    // Pre-fill all 7 days so the axis always shows the full week.
    if (activePeriod === 'W') {
      const byDate = {}
      // 1. Pre-fill 7 days (oldest → newest)
      const now = new Date()
      for (let i = 6; i >= 0; i--) {
        const d = new Date(now)
        d.setDate(d.getDate() - i)
        const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
        if (!byDate[key]) {
          byDate[key] = emptyBucket(
            `${d.getDate()} ${d.toLocaleString('en-US', { month: 'short' })}`
          )
        }
      }
      // 2. Accumulate actual data
      rawData.forEach(point => {
        const d = new Date(point.x)
        const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
        if (!byDate[key]) {
          byDate[key] = emptyBucket(
            `${d.getDate()} ${d.toLocaleString('en-US', { month: 'short' })}`
          )
        }
        Object.keys(point).forEach(k => {
          if (k === 'x') return
          byDate[key]._sums[k] = (byDate[key]._sums[k] || 0) + point[k]
          byDate[key]._counts[k] = (byDate[key]._counts[k] || 0) + 1
        })
      })
      return _aggregate(byDate)
    }

    // D view → raw timestamps; ensure x is numeric (ms) for the time-scale XAxis
    return rawData.map(pt => ({
      ...pt,
      x: typeof pt.x === 'string' ? new Date(pt.x).getTime() : Number(pt.x),
    }))
  }, [rawData, activePeriod])

  // W / M / Y all produce categorical string labels now; D is numeric timestamps
  const isCategorical = activePeriod !== 'D'
  // M has ~10 labels → rotate for readability; W has only 8 short labels → no rotation
  const rotateAxis    = activePeriod === 'M'

  const formatXAxisTick = (value) => {
    // W / M / Y: graphData[n].x is already a human-readable string label
    if (isCategorical) return String(value)
    // D: value is a raw UTC ms timestamp — display as HH:MM in customer TZ
    if (!value) return ''
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: customerTz, hour: '2-digit', minute: '2-digit', hour12: false,
    }).format(new Date(value))
  }

  // D view bounds: explicit [now − 24h, now] in UTC ms.  Captured into a memo
  // bound to `dateRange.to` so the window only re-anchors when the user re-
  // selects a period (D button) or shifts the picker — not on every render.
  // This replaces Symfony's null-sentinel trick by giving Recharts an
  // explicit numeric domain.
  const dWindowMs = useMemo(() => {
    if (activePeriod !== 'D') return null
    // Prefer the wall-clock-in-TZ strings already set on dateRange; fall back
    // to "right now in customer TZ" if those are missing.
    const endMs   = parseWallClockInTzToMs(dateRange.to,   customerTz) ?? Date.now()
    const startMs = parseWallClockInTzToMs(dateRange.from, customerTz) ?? (endMs - 24 * 60 * 60 * 1000)
    return { startMs, endMs }
  }, [activePeriod, dateRange.from, dateRange.to, customerTz])

  // D view: 2-hour tick marks anchored to even-hour boundaries IN THE
  // CUSTOMER'S TZ.  e.g. for Asia/Kolkata with now=04:30 IST → 04:00 prev
  // day, 06:00, 08:00 ... 02:00, 04:00 IST.
  const dTicks = useMemo(() => {
    if (activePeriod !== 'D' || !dWindowMs) return undefined
    const { startMs, endMs } = dWindowMs
    // Find the first even-hour boundary at-or-after startMs, evaluated in TZ.
    const startParts = wallClockPartsInTz(new Date(startMs), customerTz)
    // Round up minute/second to next even hour.
    const roundUpHour = startParts.minute === 0 && startParts.second === 0
      ? (startParts.hour % 2 === 0 ? startParts.hour : startParts.hour + 1)
      : startParts.hour + (startParts.hour % 2 === 0 ? 2 : 1)
    // Build the first tick as wall-clock in customer TZ → UTC ms.
    const firstTickStr =
      `${startParts.year}-${String(startParts.month).padStart(2,'0')}-` +
      `${String(startParts.day).padStart(2,'0')} ` +
      `${String(roundUpHour % 24).padStart(2,'0')}:00:00`
    // If roundUpHour wrapped past 24, advance the date by one day.
    const dayAdvance = Math.floor(roundUpHour / 24)
    let firstTickMs = parseWallClockInTzToMs(firstTickStr, customerTz)
    if (firstTickMs == null) return undefined
    firstTickMs += dayAdvance * 24 * 60 * 60 * 1000
    const ticks = []
    for (let t = firstTickMs; t <= endMs; t += 2 * 60 * 60 * 1000) ticks.push(t)
    return ticks
  }, [activePeriod, dWindowMs, customerTz])

  const formatTooltipLabel = (value) => {
    if (isCategorical) return String(value)
    if (!value) return ''
    return new Intl.DateTimeFormat('en-US', {
      timeZone: customerTz,
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    }).format(new Date(value))
  }

  // Vertical-axis settings are now per-parameter (set in the Graph Editor).
  // For the single Y-axis chart we use the first selected parameter's axis
  // (exact for one parameter; the first one's scale for combined charts).
  const axisSettings = useMemo(() => {
    const first = selectedParameters[0]
    return (first && graphSettings.series?.[first.id]) || {}
  }, [selectedParameters, graphSettings.series])

  const yDomain = useMemo(() => {
    const min = axisSettings.yMin != null ? axisSettings.yMin : 'auto'
    const max = axisSettings.yMax != null ? axisSettings.yMax : 'auto'
    return [min, max]
  }, [axisSettings])

  const yTicks = useMemo(() => {
    if (axisSettings.yMin != null && axisSettings.yMax != null && axisSettings.ySteps) {
      const step = (axisSettings.yMax - axisSettings.yMin) / axisSettings.ySteps
      return Array.from({ length: axisSettings.ySteps + 1 }, (_, i) =>
        +(axisSettings.yMin + i * step).toFixed(4)
      )
    }
    return undefined
  }, [axisSettings])

  // ── Format timestamp for table rows ─────────────────────────────────────────
  const formatTimeLabel = (value) => {
    if (isCategorical) return String(value)
    if (!value) return ''
    return new Date(value).toLocaleString()
  }

  // ── Statistics (per parameter): count, min, max, avg, median, stddev ────────
  const statistics = useMemo(() => {
    if (!graphData.length || !selectedParameters.length) return []
    return selectedParameters.map((param, idx) => {
      const key = `p_${param.id}`
      const values = graphData.map(d => d[key]).filter(v => v != null && !isNaN(v))
      if (!values.length) return { param, idx, count: 0, name: seriesMetadata[param.id]?.name || param.name, unit: seriesMetadata[param.id]?.unit || '' }
      const count = values.length
      const sum   = values.reduce((a, b) => a + b, 0)
      const avg   = sum / count
      const min   = Math.min(...values)
      const max   = Math.max(...values)
      const sorted = [...values].sort((a, b) => a - b)
      const median = count % 2 === 0 ? (sorted[count/2-1] + sorted[count/2]) / 2 : sorted[Math.floor(count/2)]
      const stddev = Math.sqrt(values.reduce((a, b) => a + (b - avg) ** 2, 0) / count)
      return {
        param, idx, count,
        avg: +avg.toFixed(4), min: +min.toFixed(4), max: +max.toFixed(4),
        median: +median.toFixed(4), stddev: +stddev.toFixed(4),
        name: seriesMetadata[param.id]?.name || param.name,
        unit: seriesMetadata[param.id]?.unit || '',
      }
    })
  }, [graphData, selectedParameters, seriesMetadata])

  // ── Download helpers ─────────────────────────────────────────────────────────

  // Append anchor to DOM before click — required in modern Chrome/Firefox
  const _triggerDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.style.display = 'none'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    setTimeout(() => URL.revokeObjectURL(url), 2000)
  }

  // Build header + data rows for CSV / XLS
  const _buildRows = () => {
    const hdr = ['"Time"', ...selectedParameters.map(p => {
      const m = seriesMetadata[p.id] || {}
      const name = (m.name || p.name).replace(/"/g, '""')
      const unit = m.unit ? ` (${m.unit})` : ''
      return `"${name}${unit}"`
    })]
    // Only include rows that have at least one real value
    const body = graphData
      .filter(pt => selectedParameters.some(p => pt[`p_${p.id}`] != null))
      .map(pt => {
        const t = `"${formatTimeLabel(pt.x)}"`
        const vals = selectedParameters.map(p => {
          const v = pt[`p_${p.id}`]
          const prec = seriesMetadata[p.id]?.precision ?? 0
          return v != null ? Number(v).toFixed(prec) : ''
        })
        return [t, ...vals]
      })
    return [hdr, ...body]
  }

  const downloadCSV = () => {
    setShowMenu(false)
    try {
      const rows = _buildRows()
      // rows[0] = header; if no data rows, still allow download (header only)
      const csv = rows.map(r => r.join(',')).join('\r\n')
      _triggerDownload(
        new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' }),
        `${widget.title || 'data'}.csv`
      )
    } catch (e) { console.error('[CSV]', e) }
  }

  const downloadXLS = () => {
    setShowMenu(false)
    try {
      const rows = _buildRows()
      const tsv = rows.map(r =>
        r.map(c => String(c).replace(/^"|"$/g, '').replace(/""/g, '"')).join('\t')
      ).join('\r\n')
      _triggerDownload(
        new Blob(['﻿' + tsv], { type: 'application/vnd.ms-excel;charset=utf-8;' }),
        `${widget.title || 'data'}.xls`
      )
    } catch (e) { console.error('[XLS]', e) }
  }

  const downloadPNG = () => {
    setShowMenu(false)
    // Chart always renders inline (data table + statistics are modals now),
    // so the SVG is always present in the DOM — no view-mode juggling needed.
    setTimeout(() => {
      try {
        const svgEl = chartContainerRef.current?.querySelector('svg')
        if (!svgEl) { console.warn('[PNG] No SVG found'); return }

        // Clone + set explicit pixel dimensions (width="100%" breaks canvas draw)
        const rect = svgEl.getBoundingClientRect()
        const W = Math.round(rect.width)  || 800
        const H = Math.round(rect.height) || 400
        const clone = svgEl.cloneNode(true)
        clone.setAttribute('width', W)
        clone.setAttribute('height', H)
        clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg')

        // Use Blob URL (avoids encodeURIComponent size limit)
        const svgBlob = new Blob([new XMLSerializer().serializeToString(clone)], { type: 'image/svg+xml;charset=utf-8' })
        const svgUrl  = URL.createObjectURL(svgBlob)

        const canvas = document.createElement('canvas')
        canvas.width  = W * 2   // 2× for retina
        canvas.height = H * 2
        const ctx = canvas.getContext('2d')
        ctx.scale(2, 2)
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, W, H)

        const img = new Image()
        img.onload = () => {
          ctx.drawImage(img, 0, 0, W, H)
          URL.revokeObjectURL(svgUrl)
          canvas.toBlob(b => {
            if (b) _triggerDownload(b, `${widget.title || 'chart'}.png`)
          }, 'image/png')
        }
        img.onerror = (err) => {
          URL.revokeObjectURL(svgUrl)
          console.error('[PNG] Image load failed', err)
        }
        img.src = svgUrl
      } catch (e) { console.error('[PNG]', e) }
    }, 80)
  }

  // ─── Print-window helper ───────────────────────────────────────────────────
  // Opens a child window, writes the supplied HTML body, and triggers the
  // browser's print dialog. The same primitive backs Print Chart, Print Data
  // Table, and Print Statistics — the caller supplies the body markup.
  const _openPrintWindowWithBody = (bodyHTML, { closeAfter, titleSuffix = '' } = {}) => {
    setShowMenu(false)
    // Must be synchronous from click — popup blockers allow this
    const win = window.open('', '_blank', 'width=1024,height=768,menubar=no,toolbar=no')
    if (!win) { alert('Pop-up blocked. Please allow pop-ups for this site and try again.'); return }

    const baseTitle = widget.title || 'Chart'
    const title     = titleSuffix ? `${baseTitle} — ${titleSuffix}` : baseTitle
    const subtitle  = `Period: ${fromDate || '—'} → ${toDate || '—'}`

    win.document.open()
    win.document.write(`<!DOCTYPE html>
<html><head>
  <meta charset="utf-8">
  <title>${title}</title>
  <style>
    * { box-sizing: border-box; }
    body { margin: 24px; font-family: Arial, sans-serif; background: #fff; color: #222; }
    h2 { margin: 0 0 4px; font-size: 18px; }
    p.sub { margin: 0 0 16px; font-size: 12px; color: #888; }
    svg { max-width: 100%; height: auto; display: block; }
    table { border-collapse: collapse; width: 100%; font-size: 11px; }
    th, td { border: 1px solid #d0d0d0; padding: 6px 8px; text-align: left; vertical-align: top; }
    thead th { background: #f3f4f6; font-weight: 600; }
    tbody tr:nth-child(even) td { background: #fafafa; }
    .col-head { font-weight: 600; }
    .col-head-sub { color: #888; font-weight: 400; font-size: 10px; }
    @media print { body { margin: 12px; } }
  </style>
</head><body>
  <h2>${title}</h2>
  <p class="sub">${subtitle}</p>
  ${bodyHTML}
</body></html>`)
    win.document.close()

    // Trigger print after content is fully loaded
    win.addEventListener('load', () => {
      win.focus()
      win.print()
      if (closeAfter) setTimeout(() => win.close(), 500)
    })
    // Fallback: some browsers fire load before document.close
    setTimeout(() => { try { win.focus(); win.print() } catch (_) {} }, 800)
  }

  // Build the chart SVG markup for printing (used by Print Chart / Download
  // PDF on the chart-context menu).
  const _buildChartHtml = () => {
    const svgEl = chartContainerRef.current?.querySelector('svg')
    if (!svgEl) {
      return '<p style="color:#888">Chart not available.</p>'
    }
    const clone = svgEl.cloneNode(true)
    const rect  = svgEl.getBoundingClientRect()
    clone.setAttribute('width',  Math.round(rect.width)  || 900)
    clone.setAttribute('height', Math.round(rect.height) || 420)
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
    return clone.outerHTML
  }

  // Escape a string for safe inclusion in a print-window HTML body.
  const _esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ))

  // Build the data-table HTML for printing — mirrors the structured
  // Site / Device / Sensor / Parameter header the modal renders.
  const _buildDataTableHtml = () => {
    const cols = _buildDataTableColumns()
    const headerCells = cols.map(col =>
      `<th>${col.headerLines.map((l, i) =>
        `<div class="${i === 0 ? 'col-head' : 'col-head-sub'}">${_esc(l)}</div>`
      ).join('')}</th>`
    ).join('')
    const rows = graphData.map(pt => {
      const cells = cols.map(col => {
        const v = col.accessor(pt)
        return `<td>${v == null || v === '' ? '—' : _esc((+v).toFixed(col.precision ?? 0))}</td>`
      }).join('')
      return `<tr><td>${_esc(formatTimeLabel(pt.x))}</td>${cells}</tr>`
    }).join('')
    return `
<table>
  <thead><tr><th>Timestamp</th>${headerCells}</tr></thead>
  <tbody>${rows || `<tr><td colspan="${cols.length + 1}" style="text-align:center;color:#888">No data</td></tr>`}</tbody>
</table>`
  }

  // Build the statistics HTML for printing.
  const _buildStatsHtml = () => {
    const headers = ['Parameter', 'Unit', 'Count', 'Min', 'Max', 'Average', 'Median', 'Std Dev']
    const rows = statistics.map(s => `
      <tr>
        <td>${_esc(s.name)}</td>
        <td>${_esc(s.unit || '—')}</td>
        <td>${_esc(s.count?.toLocaleString() ?? '—')}</td>
        <td>${_esc(s.min ?? '—')}</td>
        <td>${_esc(s.max ?? '—')}</td>
        <td>${_esc(s.avg ?? '—')}</td>
        <td>${_esc(s.median ?? '—')}</td>
        <td>${_esc(s.stddev ?? '—')}</td>
      </tr>`).join('')
    return `
<table>
  <thead><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr></thead>
  <tbody>${rows || `<tr><td colspan="${headers.length}" style="text-align:center;color:#888">No statistics</td></tr>`}</tbody>
</table>`
  }

  // ─── Chart-context exports (header menu) ──────────────────────────────────
  const downloadPDF = () => _openPrintWindowWithBody(_buildChartHtml(), { closeAfter: true })
  const printChart  = () => _openPrintWindowWithBody(_buildChartHtml(), { closeAfter: false })

  // ─── Data-Table-context exports (modal menu) ─────────────────────────────
  const downloadDataTablePDF = () =>
    _openPrintWindowWithBody(_buildDataTableHtml(), { closeAfter: true,  titleSuffix: 'Data Table' })
  const printDataTable        = () =>
    _openPrintWindowWithBody(_buildDataTableHtml(), { closeAfter: false, titleSuffix: 'Data Table' })

  // ─── Statistics-context exports (modal menu) ─────────────────────────────
  const downloadStatsPDF = () =>
    _openPrintWindowWithBody(_buildStatsHtml(), { closeAfter: true,  titleSuffix: 'Statistics' })
  const printStatistics  = () =>
    _openPrintWindowWithBody(_buildStatsHtml(), { closeAfter: false, titleSuffix: 'Statistics' })

  // Stats-row CSV / XLS — schema differs from time-series rows above.
  const _buildStatsRows = () => {
    const hdr = ['Parameter', 'Unit', 'Count', 'Min', 'Max', 'Average', 'Median', 'Std Dev']
    const body = statistics.map(s => [
      s.name, s.unit || '', s.count ?? '', s.min ?? '', s.max ?? '', s.avg ?? '', s.median ?? '', s.stddev ?? '',
    ])
    return [hdr, ...body]
  }
  const downloadStatsCSV = () => {
    try {
      const csv = _buildStatsRows().map(r =>
        r.map(c => /[",\n]/.test(String(c)) ? `"${String(c).replace(/"/g, '""')}"` : c).join(',')
      ).join('\r\n')
      _triggerDownload(
        new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' }),
        `${widget.title || 'statistics'}-statistics.csv`
      )
    } catch (e) { console.error('[StatsCSV]', e) }
  }
  const downloadStatsXLS = () => {
    try {
      const tsv = _buildStatsRows().map(r => r.join('\t')).join('\r\n')
      _triggerDownload(
        new Blob(['﻿' + tsv], { type: 'application/vnd.ms-excel;charset=utf-8;' }),
        `${widget.title || 'statistics'}-statistics.xls`
      )
    } catch (e) { console.error('[StatsXLS]', e) }
  }

  // ─── Structured column descriptor (Site → Device → Sensor → Param/Unit) ──
  // Used by both the modal table view and the data-table print export.
  const _buildDataTableColumns = () => selectedParameters.map((p) => {
    const m = seriesMetadata[p.id] || {}
    const headerLines = [
      // Line 1 (bold) — site name when known, otherwise parameter name as fallback
      m.siteName || p.siteName || p.site_name || (m.name || p.name),
      // Optional context lines — device UID and sensor name when available
      p.deviceUid || p.device_uid || null,
      p.sensorName || p.sensor_name || null,
      // Parameter (Unit) — always the last line
      `${m.name || p.name}${m.unit ? ` (${m.unit})` : ''}`,
    ].filter(Boolean)
    return {
      paramId: p.id,
      headerLines,
      precision: m.precision ?? 0,
      accessor: (row) => row[`p_${p.id}`],
    }
  })

  return (
    <>
      <XDCard
        title={widget.title}
        subtitle={widget.description}
        headerRight={
          <div className="flex items-center gap-2 flex-wrap">
            {/* D / W / M / Y period buttons */}
            <div className="flex items-center gap-0.5 bg-gray-100 rounded px-1 py-0.5 border border-gray-200">
              {['D', 'W', 'M', 'Y'].map(p => (
                <button
                  key={p}
                  onClick={() => handlePeriodButtonClick(p)}
                  className={`px-2.5 py-0.5 text-xs font-semibold rounded transition ${
                    activePeriod === p ? 'bg-[#4D4D4D] text-white' : 'text-gray-600 hover:bg-gray-200'
                  }`}
                >{p}</button>
              ))}
            </div>

            {/* Start Date */}
            <div className="flex flex-col" style={{ minWidth: 90 }}>
              <span className="text-[9px] text-gray-400 font-medium leading-none mb-0.5">Start Date</span>
              <input
                type="date"
                value={fromDate}
                onChange={e => setDateRange?.(prev => ({ ...prev, from: e.target.value + ' 00:00:00' }))}
                className="text-xs border border-gray-200 rounded px-1.5 py-0.5 focus:outline-none focus:border-[#358ECD] bg-white"
                style={{ fontSize: 11 }}
              />
            </div>

            {/* End Date */}
            <div className="flex flex-col" style={{ minWidth: 90 }}>
              <span className="text-[9px] text-gray-400 font-medium leading-none mb-0.5">End Date</span>
              <input
                type="date"
                value={toDate}
                onChange={e => setDateRange?.(prev => ({ ...prev, to: e.target.value + ' 23:59:59' }))}
                className="text-xs border border-gray-200 rounded px-1.5 py-0.5 focus:outline-none focus:border-[#358ECD] bg-white"
                style={{ fontSize: 11 }}
              />
            </div>

            {/* Three-dot menu */}
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setShowMenu(v => !v)}
                className="p-1 text-gray-400 hover:text-gray-600 rounded transition"
                title="More options"
              >
                <svg width="16" height="16" viewBox="0 0 4 16" fill="currentColor">
                  <circle cx="2" cy="2" r="1.5"/><circle cx="2" cy="8" r="1.5"/><circle cx="2" cy="14" r="1.5"/>
                </svg>
              </button>
              {showMenu && (
                <div className="absolute right-0 top-full mt-1 w-52 bg-white border border-gray-200 rounded-xl shadow-2xl z-[9999] py-1.5 text-[13px] text-gray-700">
                  {/* ── Detail popups ── */}
                  <button
                    className="w-full text-left px-4 py-2 hover:bg-gray-50 transition"
                    onClick={() => { setTableModalOpen(true); setShowMenu(false) }}
                  >
                    View Data Table
                  </button>
                  <button
                    className="w-full text-left px-4 py-2 hover:bg-gray-50 transition"
                    onClick={() => { setStatsModalOpen(true); setShowMenu(false) }}
                  >
                    View Statistics
                  </button>

                  <div className="my-1.5 border-t border-gray-100"/>

                  {/* ── Downloads (chart context) ── */}
                  <button className="w-full text-left px-4 py-2 hover:bg-gray-50 transition" onClick={downloadPDF}>Download PDF</button>
                  <button className="w-full text-left px-4 py-2 hover:bg-gray-50 transition" onClick={downloadPNG}>Download PNG Image</button>
                  <button className="w-full text-left px-4 py-2 hover:bg-gray-50 transition" onClick={downloadCSV}>Download CSV</button>
                  <button className="w-full text-left px-4 py-2 hover:bg-gray-50 transition" onClick={downloadXLS}>Download XLS</button>

                  <div className="my-1.5 border-t border-gray-100"/>

                  <button className="w-full text-left px-4 py-2 hover:bg-gray-50 transition" onClick={printChart}>Print Line Chart</button>
                </div>
              )}
            </div>

            {/* Pencil / edit icon — hidden while in Customize mode */}
            {!isCustomizing && (
              <button
                onClick={() => setShowEditor(true)}
                className="p-1 text-gray-400 hover:text-[#358ECD] rounded transition"
                title="Edit graph settings"
              >
                <EditIcon />
              </button>
            )}
          </div>
        }
      >
        {/* ── Large-dataset warning banner ── */}
        {dataWarning && (
          <div className="mx-4 mt-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700 flex items-start gap-2">
            <span className="shrink-0 mt-0.5">⚠️</span>
            <span className="flex-1">{dataWarning}</span>
            <button className="shrink-0 text-amber-400 hover:text-amber-600 font-bold" onClick={() => setDataWarning(null)}>✕</button>
          </div>
        )}

        {/* ── Loading ── */}
        {loading ? (
          <div className="h-64 flex items-center justify-center">
            <Loader className="w-5 h-5 animate-spin text-[#358ECD]" />
          </div>

        ) : selectedParameters.length === 0 ? (
          <div className="h-64 flex items-center justify-center text-sm text-gray-400 px-4 text-center">
            Select parameters from the left panel to view sensor data
          </div>

        ) : (activePeriod === 'D' ? graphData.length === 0 : rawData.length === 0) ? (
          <div className="h-64 flex items-center justify-center text-sm text-gray-400">No data available for this period</div>

        /* ── Chart (always rendered inline; Data Table & Statistics are modals) ── */
        ) : (
          <div ref={chartContainerRef} style={{ width: '100%', height: 320 }}>
            <ResponsiveContainer width="100%" height={320}>
              <ComposedChart data={graphData} margin={{ top: 10, right: 20, left: 0, bottom: rotateAxis ? 60 : 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="x"
                  type={isCategorical ? 'category' : 'number'}
                  {...(!isCategorical ? {
                    scale: 'time',
                    // D-view: explicit [now − 24h, now] domain in UTC ms so
                    // the axis ALWAYS spans the full rolling window, even
                    // when the device first reported partway through it.
                    // This is what kills the leading whitespace bug.
                    domain: dWindowMs
                      ? [dWindowMs.startMs, dWindowMs.endMs]
                      : ['dataMin', 'dataMax'],
                    allowDataOverflow: false,
                    ...(dTicks ? { ticks: dTicks } : {}),
                  } : {})}
                  tickFormatter={isCategorical ? undefined : formatXAxisTick}
                  tick={{ fontSize: 11, fill: '#888' }}
                  angle={rotateAxis ? -45 : 0}
                  textAnchor={rotateAxis ? 'end' : 'middle'}
                  height={rotateAxis ? 70 : 40}
                  interval={
                    activePeriod === 'D' ? 0          // show all 2-hour tick marks
                    : activePeriod === 'W' ? 0         // show all 7–8 day labels
                    : activePeriod === 'M' ? 2         // every 3rd label → ~3-day gap
                    : /* Y */ 0                        // show all 12 months
                  }
                  minTickGap={activePeriod === 'D' ? 0 : activePeriod === 'Y' ? 5 : 10}
                />
                <YAxis tick={{ fontSize: 11, fill: '#888' }} width={45} domain={yDomain} {...(yTicks ? { ticks: yTicks } : {})} />
                <Tooltip
                  labelFormatter={formatTooltipLabel}
                  formatter={(value, key) => {
                    const m = seriesMetadata[key.replace('p_', '')] || {}
                    return [`${value?.toFixed(m.precision ?? 0) ?? '-'} ${m.unit}`, m.name || key]
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} formatter={key => seriesMetadata[key.replace('p_', '')]?.name || key} />
                {selectedParameters.map((param, idx) => {
                  const ss = graphSettings.series?.[param.id] || {}
                  const lineColor = ss.color || COLORS[idx % COLORS.length]
                  return (
                    <Line
                      key={param.id}
                      type="monotone"
                      dataKey={`p_${param.id}`}
                      stroke={lineColor}
                      strokeWidth={2}
                      dot={makeDot(ss.pointShape || 'Circle', lineColor)}
                      isAnimationActive={false}
                      connectNulls
                      name={`p_${param.id}`}
                    />
                  )
                })}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </XDCard>

      {/* Graph Editor Modal */}
      <GraphEditorModal
        open={showEditor}
        onClose={() => setShowEditor(false)}
        onSave={newSettings => { setGraphSettings(newSettings); lsSetScoped('wq_graph', projectId, widget.id, newSettings) }}
        selectedParameters={selectedParameters}
        seriesMetadata={seriesMetadata}
        settings={graphSettings}
        widget={widget}
      />

      {/* Data Table popup — opened from the chart's `⋮` menu */}
      <DataTableModal
        open={tableModalOpen}
        onClose={() => setTableModalOpen(false)}
        title={`${widget.title || 'Data Table'} — Data Table`}
        data={graphData}
        columns={_buildDataTableColumns()}
        formatTimestamp={formatTimeLabel}
        decimals={2}
        downloadPDF={downloadDataTablePDF}
        downloadPNG={downloadPNG}
        downloadCSV={downloadCSV}
        downloadXLS={downloadXLS}
        printDataTable={printDataTable}
      />

      {/* Statistics popup — opened from the chart's `⋮` menu */}
      <StatisticsModal
        open={statsModalOpen}
        onClose={() => setStatsModalOpen(false)}
        title={`${widget.title || 'Statistics'} — Statistics`}
        statistics={statistics}
        seriesColor={(i) => {
          const param = selectedParameters[i]
          return graphSettings.series?.[param?.id]?.color || COLORS[i % COLORS.length]
        }}
        downloadPDF={downloadStatsPDF}
        downloadPNG={downloadPNG}
        downloadCSV={downloadStatsCSV}
        downloadXLS={downloadStatsXLS}
        printStatistics={printStatistics}
      />
    </>
  )
}

/**
 * Environmental News Widget
 */
export function EnvironmentalNewsWidget({ widget, data }) {
  const articles = data?.articles || [
    { title: 'From Source to Sea: Monitoring and Protecting...', url: '#' },
    { title: 'From Source to Sea: Monitoring and Protecting...', url: '#' },
    { title: 'Using Traditional Ghanaian Fishing Canoes to Monitor...', url: '#' },
  ]
  return (
    <XDCard
      title={widget.title}
      subtitle={widget.description}
      headerRight={
        <button className="text-[#4D4D4D] flex items-center gap-2 font-medium text-sm bg-gray-200 py-1.5 px-4 rounded-lg">
          <ExternalLinkIcon /> View All
        </button>
      }
    >
      <div className="divide-y divide-gray-100 px-6">
        {articles.map((a, i) => (
          <div key={i} className="py-4">
            <p className="text-base font-medium text-gray-900 line-clamp-2">{a.title}</p>
            <a href={a.url} className="text-gray-600 font-medium text-sm mt-1 hover:underline inline-block">Read Article</a>
          </div>
        ))}
      </div>
    </XDCard>
  )
}

/**
 * Event Monitor Widget
 */
export function EventMonitorWidget({ widget, data }) {
  // Delegate to the same EventMonitorPanel the System Performance dashboard
  // uses, so the Overview shows real alarms instead of the hard-coded
  // (Low Power / Low Dissolved Oxygen / High Turbidity) fallback. The
  // panel renders its own card, header, Filter dropdown, inline edit mode,
  // and settings modal — we just wire the project-scoped data through.
  const { projectId } = useParams()
  const [alarms, setAlarms] = useState(Array.isArray(data?.events) ? data.events : [])
  const [loading, setLoading] = useState(false)
  const [sites, setSites] = useState([])

  const fetchAlarms = useCallback(() => {
    if (!projectId) return
    setLoading(true)
    alarmApi.getAlarms({ project: projectId })
      .then((res) => setAlarms(
        Array.isArray(res.data) ? res.data : (res.data?.results || [])
      ))
      .catch(() => setAlarms([]))
      .finally(() => setLoading(false))
  }, [projectId])

  useEffect(() => {
    fetchAlarms()
  }, [fetchAlarms])

  useEffect(() => {
    if (!projectId) return
    projectsApi.fetchProjectSites(Number(projectId))
      .then((res) => setSites(Array.isArray(res) ? res : []))
      .catch(() => setSites([]))
  }, [projectId])

  return (
    <EventMonitorPanel
      alarms={alarms}
      loading={loading}
      onChanged={fetchAlarms}
      sites={sites}
      projectId={projectId}
    />
  )
}

/**
 * Support / Donation Widget — editable sponsor grid.
 *
 *   - Donate Now link at top (URL configured via editor)
 *   - 2-column grid of sponsor logos, each linking to its domain
 *   - Edit pencil opens SupportProjectEditorModal (add / remove / drag-reorder
 *     sponsors; each sponsor has a logo URL or uploaded image + domain link)
 *   - Persists to localStorage `wq_support_${widget.id}` (matches every
 *     other configurable widget in this file)
 */
export function SupportProjectWidget({ widget, data }) {
  const { projectId } = useParams()
  const { isCustomizing = false } = useDashboardContext()

  // Project-scoped key — see scopedLsKey doc-block. Without this every
  // project sharing the Overview template would render the same sponsor
  // list because widget.id collides across projects.
  const lsValue = lsGetScoped('wq_support', projectId, widget.id, null)
  const [donateUrl, setDonateUrl] = useState(lsValue?.donateUrl ?? data?.donateUrl ?? '')
  const [sponsors, setSponsors] = useState(lsValue?.sponsors ?? data?.sponsors ?? [])
  const [showEditor, setShowEditor] = useState(false)

  const handleSave = ({ donateUrl: url, sponsors: list }) => {
    setDonateUrl(url)
    setSponsors(list)
    lsSetScoped('wq_support', projectId, widget.id, { donateUrl: url, sponsors: list })
  }

  return (
    <>
      <XDCard
        title={widget.title || 'Support Our Project'}
        subtitle={widget.description}
        headerRight={!isCustomizing ? (
          <button
            onClick={() => setShowEditor(true)}
            className="p-1 text-gray-400 hover:text-[#358ECD] rounded transition"
            title="Edit sponsors"
          >
            <EditIcon />
          </button>
        ) : null}
      >
        <div className="p-4 flex flex-col gap-3 h-full">
          {donateUrl && (
            <a
              href={donateUrl}
              target="_blank"
              rel="noreferrer"
              className="text-[#0866AB] font-medium text-sm underline hover:text-[#054b80]"
            >
              Donate Now
            </a>
          )}

          {sponsors.length === 0 ? (
            <div className="flex flex-col items-center justify-center flex-1 py-6 text-gray-400 gap-2">
              <span className="text-2xl">🤝</span>
              <p className="text-xs">No sponsors yet — click the edit (✎) icon to add some.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 auto-rows-min">
              {sponsors.map((s, idx) => {
                const Inner = (
                  <div className="flex items-center justify-center bg-white border border-gray-100 rounded p-3 hover:border-gray-300 transition" style={{ minHeight: 96 }}>
                    <img
                      src={s.logo}
                      alt={s.name || `Sponsor ${idx + 1}`}
                      className="max-h-20 max-w-full object-contain"
                      onError={(e) => { e.target.style.opacity = 0.3 }}
                    />
                  </div>
                )
                return s.link ? (
                  <a
                    key={s.id || idx}
                    href={s.link}
                    target="_blank"
                    rel="noreferrer"
                    title={s.name}
                  >
                    {Inner}
                  </a>
                ) : (
                  <div key={s.id || idx} title={s.name}>{Inner}</div>
                )
              })}
            </div>
          )}
        </div>
      </XDCard>

      <SupportProjectEditorModal
        open={showEditor}
        onClose={() => setShowEditor(false)}
        onSave={handleSave}
        initialDonateUrl={donateUrl}
        initialSponsors={sponsors}
      />
    </>
  )
}

/**
 * Disclaimer Widget — editable rich-text panel.
 *
 * Mirrors the Project Description pattern: clicking the edit pencil
 * calls the shared `onConfigure` handler which opens WidgetConfigModal
 * with a rich-text editor. Saved content is stored on the widget's
 * `rich_content` field and rendered as HTML below.
 */
const DEFAULT_DISCLAIMER_TEXT = 'Uncertainty and potential for error can be associated with environmental monitoring data. Data users are cautioned to consider carefully the provisional nature of the information before using it for decisions that concern personal or public safety or the conduct of business that involves substantial monetary or operational consequences. No warranty, express or implied, is given as to the accuracy, reliability, utility or completeness of the data hosted on this datacenter.'

export function DisclaimerWidget({ widget, data, onConfigure }) {
  const { isCustomizing = false } = useDashboardContext()
  const html = widget.custom_rich_content || widget.rich_content || ''
  const fallbackText = data?.text || DEFAULT_DISCLAIMER_TEXT
  return (
    <XDCard
      title={widget.title || 'Disclaimer'}
      headerRight={!isCustomizing && onConfigure ? (
        <button
          onClick={() => onConfigure(widget)}
          className="p-1 text-gray-400 hover:text-[#358ECD] rounded transition"
          title="Edit disclaimer"
        >
          <EditIcon />
        </button>
      ) : null}
    >
      <div className="p-6 text-gray-600 text-sm leading-relaxed">
        {html ? (
          <div
            className="prose prose-sm max-w-none rich-content-render"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        ) : (
          <p>{fallbackText}</p>
        )}
      </div>
    </XDCard>
  )
}

// ─── Knowledge Base — fallback demo list + helper modals ────────────────────
// Fallback shown when the backend is unreachable (e.g. gunicorn restart in
// progress) or hasn't run the KB migration yet. Mirrors the legacy XD demo
// articles so the widget never renders empty / broken on first load.
const KB_FALLBACK_ARTICLES = [
  { id: 'demo-1', title: 'X3 User Guide',            summary: '' },
  { id: 'demo-2', title: 'EWC Integration Guide',    summary: '' },
  { id: 'demo-3', title: 'XB-200 User Guide',        summary: '' },
  { id: 'demo-4', title: 'NX260 Integration Guide',  summary: '' },
  { id: 'demo-5', title: 'XB-200 Getting Started Video', summary: '' },
  { id: 'demo-6', title: 'X3 FAQ',                   summary: '' },
]

/**
 * Full-article detail modal. Loads the body via kbApi.getArticle when a
 * numeric backend id is passed; for fallback (string-id) entries it just
 * shows a stub message explaining the backend isn't connected.
 */
function KnowledgeBaseDetailModal({ open, articleId, fallbackTitle, onClose }) {
  const [loading, setLoading] = useState(false)
  const [article, setArticle] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!open || !articleId) return
    // Demo / fallback ids start with "demo-" — never hit the API for those.
    if (typeof articleId === 'string' && articleId.startsWith('demo-')) {
      setArticle({ title: fallbackTitle || 'Article', body: null })
      return
    }
    setLoading(true); setError(null); setArticle(null)
    kbApi.getArticle(articleId)
      .then((res) => setArticle(res.data))
      .catch((e) => setError(e?.response?.status === 404 ? 'Article not found.' : 'Could not load article.'))
      .finally(() => setLoading(false))
  }, [open, articleId, fallbackTitle])

  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null
  return (
    <div className="fixed inset-0 z-[2000] bg-black/40 flex items-start justify-center overflow-y-auto py-8" onClick={onClose}>
      <div className="bg-white w-full max-w-3xl rounded shadow-lg mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-800 truncate pr-4">
            {article?.title || fallbackTitle || 'Article'}
          </h2>
          <button type="button" onClick={onClose} className="p-1 text-gray-500 hover:text-gray-700" aria-label="Close">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div className="px-6 py-5 max-h-[70vh] overflow-y-auto">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-8 justify-center">
              <Loader className="w-4 h-4 animate-spin" /> Loading article…
            </div>
          )}
          {error && (
            <div className="text-sm text-red-600 py-8 text-center">{error}</div>
          )}
          {!loading && !error && article && (
            <>
              {article.summary && (
                <p className="text-sm text-gray-500 italic mb-4">{article.summary}</p>
              )}
              {article.body ? (
                // Knowledge Base bodies are admin-authored; trusted source.
                <div
                  className="prose prose-sm max-w-none text-gray-800"
                  dangerouslySetInnerHTML={{ __html: article.body }}
                />
              ) : (
                <p className="text-sm text-gray-500 italic">
                  Full article content not available yet. Add it in the
                  Django admin under Knowledge Base Articles.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * View-all modal — search-filtered list of every active article. Each row
 * has a Read Article action that hands off to the detail modal.
 */
function KnowledgeBaseViewAllModal({ open, articles, loading, onOpenArticle, onClose }) {
  const [query, setQuery] = useState('')

  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return articles
    return articles.filter((a) =>
      (a.title || '').toLowerCase().includes(q) ||
      (a.summary || '').toLowerCase().includes(q)
    )
  }, [articles, query])

  if (!open) return null
  return (
    <div className="fixed inset-0 z-[2000] bg-black/40 flex items-start justify-center overflow-y-auto py-8" onClick={onClose}>
      <div className="bg-white w-full max-w-3xl rounded shadow-lg mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-800">All Knowledge Base Articles</h2>
          <div className="flex items-center gap-3">
            <div className="relative">
              <svg className="absolute left-2.5 top-2 w-4 h-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search Articles"
                className="pl-9 pr-3 py-1.5 text-sm border border-gray-200 rounded w-64 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#358ECD]/30 focus:bg-white"
              />
            </div>
            <button type="button" onClick={onClose} className="p-1 text-gray-500 hover:text-gray-700" aria-label="Close">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        </div>
        <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-8 justify-center">
              <Loader className="w-4 h-4 animate-spin" /> Loading articles…
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-sm text-gray-400 py-8 text-center">
              {query ? `No articles match "${query}".` : 'No articles available.'}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filtered.map((a) => (
                <div key={a.id} className="py-3 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-800 truncate">{a.title}</div>
                    {a.summary && (
                      <div className="text-xs text-gray-500 truncate">{a.summary}</div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => onOpenArticle(a)}
                    className="text-xs text-[#358ECD] hover:underline font-medium flex-shrink-0"
                  >
                    Read Article
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Knowledge Base Widget
 *
 * Fetches active articles from /api/dashboard/knowledge-base/ on mount.
 * Falls back to a hardcoded demo list when the API is unreachable so the
 * dashboard never renders an empty widget (e.g. during a gunicorn restart
 * or before the KB migration has been applied on a fresh environment).
 *
 *   • Compact list inline (up to MAX_PREVIEW rows; "View All" link below
 *     when there are more articles than fit).
 *   • Read Article → KnowledgeBaseDetailModal with the full HTML body.
 *   • View All → KnowledgeBaseViewAllModal with search + scrollable list.
 *
 * Article authoring happens in the Django admin under
 * Knowledge Base Articles (admin → app_dashboard → Knowledge Base Articles).
 */
export function KnowledgeBaseWidget({ widget, data }) {
  const MAX_PREVIEW = 6
  const [articles, setArticles] = useState(() => data?.articles || [])
  const [loading, setLoading]   = useState(false)
  const [usingFallback, setUsingFallback] = useState(false)
  const [viewAllOpen, setViewAllOpen]     = useState(false)
  const [detailFor, setDetailFor]         = useState(null) // {id, title} | null

  // Initial load — fetch active articles from the backend. On any failure
  // (network down, 401, 404, 5xx, or the migration not yet applied), fall
  // back to the demo list so the widget still renders something useful.
  useEffect(() => {
    if (data?.articles?.length) return  // server-rendered data wins
    let cancelled = false
    setLoading(true)
    kbApi.listArticles()
      .then((res) => {
        if (cancelled) return
        const list = Array.isArray(res.data) ? res.data : (res.data?.results || [])
        if (list.length > 0) {
          setArticles(list)
          setUsingFallback(false)
        } else {
          // Empty list → still treat as success but render the fallback so
          // the customer sees something familiar. Admins can clear the
          // fallback by adding their own articles.
          setArticles(KB_FALLBACK_ARTICLES)
          setUsingFallback(true)
        }
      })
      .catch(() => {
        if (cancelled) return
        setArticles(KB_FALLBACK_ARTICLES)
        setUsingFallback(true)
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  // Refetch when the widget instance changes (different dashboards); the
  // article set is otherwise static across re-renders.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widget?.id])

  const preview = articles.slice(0, MAX_PREVIEW)
  const hasMore = articles.length > MAX_PREVIEW

  return (
    <XDCard
      title={widget.title || 'Knowledge Base'}
      subtitle={widget.description}
      headerRight={
        <button
          type="button"
          onClick={() => setViewAllOpen(true)}
          className="text-[#4D4D4D] flex items-center gap-2 font-medium text-sm bg-gray-200 hover:bg-gray-300 py-1.5 px-4 rounded-lg transition"
        >
          <ExternalLinkIcon /> View All
        </button>
      }
    >
      <div className="px-6">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-6 justify-center">
            <Loader className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {preview.map((a) => (
              <div key={a.id} className="py-3 flex items-center justify-between gap-4">
                <span className="text-sm text-gray-800 truncate">{a.title}</span>
                <button
                  type="button"
                  onClick={() => setDetailFor({ id: a.id, title: a.title })}
                  className="text-xs text-gray-500 hover:text-[#358ECD] font-medium flex-shrink-0"
                >
                  Read Article
                </button>
              </div>
            ))}
            {hasMore && (
              <button
                type="button"
                onClick={() => setViewAllOpen(true)}
                className="block w-full text-[11px] text-[#358ECD] hover:underline text-center py-2"
              >
                + {articles.length - MAX_PREVIEW} more — view all
              </button>
            )}
          </div>
        )}
      </div>

      <KnowledgeBaseViewAllModal
        open={viewAllOpen}
        articles={articles}
        loading={loading}
        onOpenArticle={(a) => { setViewAllOpen(false); setDetailFor({ id: a.id, title: a.title }) }}
        onClose={() => setViewAllOpen(false)}
      />
      <KnowledgeBaseDetailModal
        open={!!detailFor}
        articleId={detailFor?.id}
        fallbackTitle={detailFor?.title}
        onClose={() => setDetailFor(null)}
      />
    </XDCard>
  )
}

/**
 * Bar Chart Widget
 */
export function BarChartWidget({ widget, data }) {
  const hasData = data?.series?.length > 0
  const mock = [
    { name: 'Jan', v: 12 }, { name: 'Feb', v: 19 }, { name: 'Mar', v: 8 },
    { name: 'Apr', v: 24 }, { name: 'May', v: 16 }, { name: 'Jun', v: 21 },
  ]
  const series = hasData ? data.series : mock
  return (
    <XDCard
      title={widget.title}
      subtitle={widget.description}
      headerRight={
        <div className="flex items-center gap-1 bg-white rounded px-2 py-1 border border-gray-300">
          {['D','W','M','Y'].map(p => (
            <button key={p} className="px-2 py-1 text-xs font-medium rounded text-gray-700 hover:bg-gray-200 transition">{p}</button>
          ))}
        </div>
      }
    >
      <div className="p-4">
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={series} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#888' }} />
            <YAxis tick={{ fontSize: 11, fill: '#888' }} />
            <Tooltip />
            {hasData && data.bars?.length
              ? data.bars.map((bar, i) => <Bar key={i} dataKey={bar.key} fill={COLORS[i % COLORS.length]} radius={[3,3,0,0]} />)
              : <Bar dataKey="v" fill="#358ECD" radius={[3,3,0,0]} />
            }
          </BarChart>
        </ResponsiveContainer>
      </div>
    </XDCard>
  )
}

/**
 * Area Chart Widget
 */
export function AreaChartWidget({ widget, data }) {
  if (!data || !data.series) {
    return (
      <Card className="p-6 flex items-center justify-center h-80">
        <Loader className="animate-spin" size={24} />
      </Card>
    )
  }

  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">{widget.title}</h3>
      <ResponsiveContainer width="100%" height={300}>
        <AreaChart data={data.series}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis />
          <Tooltip />
          <Legend />
          {data.areas && data.areas.map((area, i) => (
            <Area key={i} type="monotone" dataKey={area.key} fill={COLORS[i % COLORS.length]} stroke={COLORS[i % COLORS.length]} />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </Card>
  )
}

/**
 * Pie Chart Widget
 */
export function PieChartWidget({ widget, data }) {
  if (!data || !data.series) {
    return (
      <Card className="p-6 flex items-center justify-center h-80">
        <Loader className="animate-spin" size={24} />
      </Card>
    )
  }

  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">{widget.title}</h3>
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie data={data.series} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label>
            {data.series.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </Card>
  )
}

/**
 * Data Table Widget
 */
export function DataTableWidget({ widget, data }) {
  if (!data || !data.rows) {
    return (
      <Card className="p-6 flex items-center justify-center h-80">
        <Loader className="animate-spin" size={24} />
      </Card>
    )
  }

  const columns = data.columns || Object.keys(data.rows[0] || {})

  return (
    <Card className="p-6 overflow-x-auto">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">{widget.title}</h3>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200">
            {columns.map((col) => (
              <th key={col} className="text-left py-2 px-4 font-semibold text-gray-700">
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((row, idx) => (
            <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
              {columns.map((col) => (
                <td key={`${idx}-${col}`} className="py-2 px-4 text-gray-600">
                  {row[col]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  )
}

/**
 * Map View Widget
 */
export function MapViewWidget({ widget, data }) {
  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">{widget.title}</h3>
      <div className="bg-gray-100 rounded-lg h-96 flex items-center justify-center">
        <p className="text-gray-500">Map visualization placeholder</p>
      </div>
    </Card>
  )
}

/**
 * Live Data Widget
 */
export function LiveDataWidget({ widget, data }) {
  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">{widget.title}</h3>
      <div className="space-y-3">
        {data?.values?.map((value, idx) => (
          <div key={idx} className="flex justify-between items-center p-3 bg-gray-50 rounded">
            <span className="text-gray-700">{value.label}</span>
            <span className="font-bold text-lg text-blue-600">{value.reading}</span>
          </div>
        ))}
      </div>
    </Card>
  )
}

/**
 * Site Conditions Widget
 */
export function SiteConditionsWidget({ widget, data }) {
  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">{widget.title}</h3>
      <div className="grid grid-cols-2 gap-4">
        {data?.conditions?.map((condition, idx) => (
          <div key={idx} className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border border-green-200">
            <div className="text-xs font-semibold text-gray-600 mb-1">{condition.label}</div>
            <div className="text-2xl font-bold text-green-600">{condition.value}</div>
            <div className="text-xs text-gray-500 mt-1">{condition.unit}</div>
          </div>
        ))}
      </div>
    </Card>
  )
}

/**
 * Video Feed Widget
 */
export function VideoFeedWidget({ widget, data }) {
  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">{widget.title}</h3>
      <div className="bg-gray-900 rounded-lg h-64 flex items-center justify-center">
        {data?.url ? (
          <video src={data.url} controls className="w-full h-full rounded-lg" />
        ) : (
          <p className="text-gray-400">No video feed available</p>
        )}
      </div>
    </Card>
  )
}

/**
 * Performance Metric Widget
 */
export function PerformanceMetricWidget({ widget, data }) {
  const getStatusColor = (status) => {
    switch (status) {
      case 'good': return 'text-green-600 bg-green-50'
      case 'warning': return 'text-yellow-600 bg-yellow-50'
      case 'critical': return 'text-red-600 bg-red-50'
      default: return 'text-gray-600 bg-gray-50'
    }
  }

  return (
    <Card className={`p-6 ${getStatusColor(data?.status)}`}>
      <h3 className="text-sm font-semibold text-gray-700 mb-2">{widget.title}</h3>
      <div className="text-3xl font-bold">{data?.value || '—'}</div>
      {data?.description && (
        <p className="text-xs text-gray-600 mt-2">{data.description}</p>
      )}
    </Card>
  )
}

// ============= ADDITIONAL CHART WIDGETS =============

/**
 * Smooth Curve Line Chart Widget
 */
export function SmoothCurveLineChartWidget({ widget, data }) {
  if (!data || !data.series) {
    return <Card className="p-6 flex items-center justify-center h-80"><Loader className="animate-spin" size={24} /></Card>
  }
  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">{widget.title}</h3>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data.series}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis />
          <Tooltip />
          <Legend />
          {data.lines && data.lines.map((line, i) => (
            <Line key={i} type="natural" dataKey={line.key} stroke={COLORS[i % COLORS.length]} isAnimationActive={true} />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Card>
  )
}

/**
 * Combo Chart Widget (Line + Bar)
 */
export function ComboChartWidget({ widget, data }) {
  const hasData = data?.series?.length > 0
  const mock = [
    { name: 'Jan', a: 14, b: 9 }, { name: 'Feb', a: 18, b: 12 },
    { name: 'Mar', a: 11, b: 16 }, { name: 'Apr', a: 22, b: 14 },
    { name: 'May', a: 17, b: 20 }, { name: 'Jun', a: 25, b: 18 },
  ]
  const series = hasData ? data.series : mock
  return (
    <XDCard
      title={widget.title}
      subtitle={widget.description}
      headerRight={
        <div className="flex items-center gap-1 bg-white rounded px-2 py-1 border border-gray-300">
          {['D','W','M','Y'].map(p => (
            <button key={p} className="px-2 py-1 text-xs font-medium rounded text-gray-700 hover:bg-gray-200 transition">{p}</button>
          ))}
        </div>
      }
    >
      <div className="p-4">
        <ResponsiveContainer width="100%" height={240}>
          <ComposedChart data={series} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#888' }} />
            <YAxis tick={{ fontSize: 11, fill: '#888' }} />
            <Tooltip />
            {hasData ? (
              <>
                {data.bars?.map((bar, i) => <Bar key={i} dataKey={bar.key} fill={COLORS[i % COLORS.length]} />)}
                {data.lines?.map((line, i) => <Line key={i} type="monotone" dataKey={line.key} stroke={COLORS[(i+2) % COLORS.length]} strokeWidth={2} />)}
              </>
            ) : (
              <>
                <Bar dataKey="a" fill="#358ECD" opacity={0.8} />
                <Line type="monotone" dataKey="b" stroke="#10B981" strokeWidth={2} dot={false} />
              </>
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </XDCard>
  )
}

/**
 * Multi Chart Widget — small-multiples variant of the comparison chart.
 *
 * Same toolbar shape as Combo Chart but renders one sub-chart per parameter,
 * each with a single trace, stacked vertically. Useful when the selected
 * parameters have different units / scales where overlaying them on a single
 * chart would be misleading.
 *
 * Data shape (when available):
 *   data.series = [
 *     { title: 'Pond Buoy | NX Temperature (C)', color: '#f59e0b',
 *       points: [{ x: <time>, y: <value> }, ...] },
 *     ...
 *   ]
 * If no data is provided, renders 3 sample sub-charts for preview.
 */
export function MultiChartWidget({ widget, data }) {
  const hasData = Array.isArray(data?.series) && data.series.length > 0

  // Mock preview series — matches the layout shown in the design screenshot.
  const mockBase = [3.1, 2.7, 2.4, 2.1, 1.9, 1.7, 1.5, 1.6, 1.8, 1.4, 1.7, 1.2, 0.9]
  const mockPoints = (offset) =>
    mockBase.map((v, i) => ({ x: `T${i + 1}`, y: Number((v + offset).toFixed(2)) }))
  const mock = [
    { title: 'Pond Buoy | NX Temperature (C)',        color: '#f59e0b', points: mockPoints(3) },
    { title: 'Weather Station | Air Temperature (F)', color: '#10b981', points: mockPoints(3) },
    { title: 'Weather Station | Wind Speed (mph)',    color: '#8b5cf6', points: mockPoints(3) },
  ]
  const charts = hasData ? data.series : mock

  return (
    <XDCard
      title={widget.title || 'Graph'}
      subtitle={widget.description}
      headerRight={
        <div className="flex items-center gap-1 bg-white rounded px-2 py-1 border border-gray-300">
          {['D', 'W', 'M', 'Y'].map(p => (
            <button key={p} className="px-2 py-1 text-xs font-medium rounded text-gray-700 hover:bg-gray-200 transition">{p}</button>
          ))}
        </div>
      }
    >
      <div className="p-4 space-y-5">
        {charts.length === 0 ? (
          <div className="py-8 text-center text-gray-400 text-sm">
            Select one or more parameters to view them here.
          </div>
        ) : charts.map((c, i) => (
          <div key={i}>
            <div className="text-sm font-semibold text-gray-700 mb-1">{c.title}</div>
            <ResponsiveContainer width="100%" height={130}>
              <LineChart data={c.points || []} margin={{ top: 5, right: 12, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="x" tick={{ fontSize: 10, fill: '#888' }} />
                <YAxis tick={{ fontSize: 10, fill: '#888' }} width={40} />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="y"
                  stroke={c.color || COLORS[i % COLORS.length]}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ))}
      </div>
    </XDCard>
  )
}

/**
 * Column Chart Widget
 */
export function ColumnChartWidget({ widget, data }) {
  if (!data || !data.series) {
    return <Card className="p-6 flex items-center justify-center h-80"><Loader className="animate-spin" size={24} /></Card>
  }
  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">{widget.title}</h3>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={data.series}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis />
          <Tooltip />
          <Legend />
          {data.bars && data.bars.map((bar, i) => (
            <Bar key={i} dataKey={bar.key} fill={COLORS[i % COLORS.length]} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </Card>
  )
}

/**
 * Stacked Column Chart Widget
 */
export function StackedColumnChartWidget({ widget, data }) {
  if (!data || !data.series) {
    return <Card className="p-6 flex items-center justify-center h-80"><Loader className="animate-spin" size={24} /></Card>
  }
  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">{widget.title}</h3>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={data.series}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis />
          <Tooltip />
          <Legend />
          {data.bars && data.bars.map((bar, i) => (
            <Bar key={i} dataKey={bar.key} fill={COLORS[i % COLORS.length]} stackId="stack" />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </Card>
  )
}

/**
 * Doughnut Chart Widget
 */
export function DoughnutChartWidget({ widget, data }) {
  if (!data || !data.series) {
    return <Card className="p-6 flex items-center justify-center h-80"><Loader className="animate-spin" size={24} /></Card>
  }
  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">{widget.title}</h3>
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie data={data.series} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={80} outerRadius={120} label>
            {data.series.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </Card>
  )
}

/**
 * 3D Pie Chart Widget (visual representation)
 */
export function Pie3DChartWidget({ widget, data }) {
  if (!data || !data.series) {
    return <Card className="p-6 flex items-center justify-center h-80"><Loader className="animate-spin" size={24} /></Card>
  }
  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">{widget.title}</h3>
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie data={data.series} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={110} label={({ name, value }) => `${name}: ${value}`}>
            {data.series.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </Card>
  )
}

// ============= GAUGE AND DIAL WIDGETS =============

/**
 * Wind Rose Widget — renders a real polar histogram from a wind-direction
 * + wind-speed parameter pair, with the configured colour palette. Falls
 * back to a "please configure" empty state if the user hasn't picked
 * parameters yet, and to an error state if the backend rejects the
 * request.
 *
 * Settings continue to persist to localStorage, matching every other
 * widget in this file. Moving widget config off localStorage is a
 * cross-widget refactor and is out of scope here.
 */
export function WindRoseWidget({ widget }) {
  const { projectId } = useParams()
  const {
    isCustomizing = false,
    dateRange,
  } = useDashboardContext()
  // Project-scoped key — see scopedLsKey doc-block at the top of this file.
  const [showEditor, setShowEditor] = useState(false)
  const [windRoseSettings, setWindRoseSettings] = useState(
    () => lsGetScoped('wq_windrose', projectId, widget.id, {})
  )
  const [chartData, setChartData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const handleSaveWindRose = (settings) => {
    setWindRoseSettings(settings)
    lsSetScoped('wq_windrose', projectId, widget.id, settings)
  }

  // Auto-default on first mount when no parameters have been configured.
  // Matches the spec: "Parameter selection defaults to wind speed and
  // wind direction of the first logger which has wind speed and wind
  // direction parameters." We persist the detected pair so the next
  // mount doesn't re-detect (cheap idempotency) and the editor opens
  // pre-filled.
  useEffect(() => {
    if (!projectId) return
    if (windRoseSettings.windDirectionParam && windRoseSettings.windSpeedParam) return
    let cancelled = false
    projectsApi.fetchProjectSites(Number(projectId))
      .then((sites) => {
        if (cancelled) return
        const defaults = detectWindDefaults(Array.isArray(sites) ? sites : [])
        if (!defaults) return
        const next = {
          ...windRoseSettings,
          selectedDevice:     String(defaults.deviceId),
          windSpeedParam:     String(defaults.speedParamId),
          windDirectionParam: String(defaults.directionParamId),
        }
        setWindRoseSettings(next)
        lsSetScoped('wq_windrose', projectId, widget.id, next)
      })
      .catch(() => { /* silent — widget will keep showing "select parameters" empty state */ })
    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, widget.id])

  const directionParam = windRoseSettings.windDirectionParam
  const speedParam     = windRoseSettings.windSpeedParam
  const configured     = Boolean(directionParam && speedParam)

  // Fetch the polar histogram whenever the user picks (or changes) the
  // direction/speed parameter pair or the dashboard date range moves.
  useEffect(() => {
    if (!configured) {
      setChartData(null)
      setError(null)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    projectsApi.fetchWindRoseData(directionParam, speedParam, {
      fromDate: dateRange?.from,
      toDate:   dateRange?.to,
    })
      .then((data) => { if (!cancelled) setChartData(data) })
      .catch((err) => {
        if (cancelled) return
        const msg = err?.response?.data?.message || 'Could not load wind rose data.'
        setError(msg)
        setChartData(null)
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [configured, directionParam, speedParam, dateRange?.from, dateRange?.to])

  // Map the configured-or-default keyColors map back to the band-ordered
  // array WindRoseChart wants.
  const bandColors = WIND_SPEED_BANDS.map(
    (b) => (windRoseSettings.keyColors || {})[b.key] || b.defaultColor
  )
  const bandLabels = WIND_SPEED_BANDS.map((b) => b.label)

  return (
    <>
      <XDCard
        title={windRoseSettings.widgetName || widget.title}
        subtitle={widget.description}
        headerRight={
          !isCustomizing ? (
            <button
              onClick={() => setShowEditor(true)}
              className="p-1 text-gray-400 hover:text-[#358ECD] rounded transition"
              title="Edit wind rose settings"
            >
              <EditIcon />
            </button>
          ) : null
        }
      >
        <div className="flex flex-col items-center justify-center p-4 gap-3">
          {/* Empty / loading / error / chart */}
          {!configured ? (
            <div className="h-64 flex flex-col items-center justify-center text-center text-sm text-gray-500 px-6">
              <div className="text-3xl mb-2">🌹</div>
              <p>Select a device and pick the wind-direction and wind-speed parameters in the editor to render the rose.</p>
            </div>
          ) : loading ? (
            <div className="h-64 flex items-center justify-center">
              <Loader className="w-5 h-5 animate-spin text-[#358ECD]" />
            </div>
          ) : error ? (
            <div className="h-64 flex items-center justify-center text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-4">
              {error}
            </div>
          ) : chartData && chartData.total_paired_samples === 0 ? (
            <div className="h-64 flex items-center justify-center text-sm text-gray-400">
              No paired wind observations in this period.
            </div>
          ) : chartData ? (
            <>
              <WindRoseChart
                sectors={chartData.sectors}
                bandColors={bandColors}
                bandLabels={bandLabels}
                size={300}
              />
              <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 text-[11px] text-gray-600 px-2">
                {WIND_SPEED_BANDS.map((b, i) => (
                  <span key={b.key} className="inline-flex items-center gap-1.5">
                    <span
                      className="inline-block w-3 h-3 rounded-sm border border-gray-300"
                      style={{ backgroundColor: bandColors[i] }}
                    />
                    {b.label}
                  </span>
                ))}
              </div>
              <p className="text-[10px] text-gray-400">
                {chartData.total_paired_samples.toLocaleString()} paired observations
                {chartData.dominant_sector ? ` · dominant: ${chartData.dominant_sector}` : ''}
              </p>
            </>
          ) : null}
        </div>
      </XDCard>

      <WindRoseEditorModal
        open={showEditor}
        onClose={() => setShowEditor(false)}
        onSave={handleSaveWindRose}
        projectId={projectId}
        initialSettings={windRoseSettings}
      />
    </>
  )
}

// ─── Gauge Series Editor Modal ────────────────────────────────────────────────
// XD reference (customer-shared screenshot): header "Gauge Series Editor"
// with Save / Cancel buttons in the top-right; body has Widget Name,
// Parameter Selection (Device + single Parameter dropdowns), and
// Min / Max / Steps numeric fields.
function GaugeSeriesEditorModal({ open, onClose, onSave, projectId, initialSettings = {} }) {
  const [widgetName, setWidgetName] = useState('')
  const [sites, setSites] = useState([])
  const [selectedDevice, setSelectedDevice] = useState('')
  const [selectedParameter, setSelectedParameter] = useState('')
  const [minValue, setMinValue] = useState('')
  const [maxValue, setMaxValue] = useState('')
  const [stepsValue, setStepsValue] = useState('')
  const [loading, setLoading] = useState(false)

  // Reset whenever the modal opens (always re-reads the latest saved settings)
  useEffect(() => {
    if (!open) return
    setWidgetName(initialSettings.widgetName || '')
    setSelectedDevice(initialSettings.selectedDevice || '')
    setSelectedParameter(initialSettings.selectedParameter || '')
    setMinValue(initialSettings.min ?? '')
    setMaxValue(initialSettings.max ?? '')
    setStepsValue(initialSettings.steps ?? '')
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Escape closes
  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === 'Escape') onClose?.() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  // Fetch project sites when modal opens
  useEffect(() => {
    if (!open || !projectId) return
    setLoading(true)
    projectsApi.fetchProjectSites(Number(projectId))
      .then((data) => setSites(Array.isArray(data) ? data : []))
      .catch(() => setSites([]))
      .finally(() => setLoading(false))
  }, [open, projectId])

  // Flatten all devices across sites
  const allDevices = useMemo(() => {
    const devs = []
    sites.forEach((site) => {
      ;(site.devices || []).forEach((dev) => {
        devs.push({ ...dev, siteName: site.name || site.station_name || '' })
      })
    })
    return devs
  }, [sites])

  // Parameters of the selected device — flattened across sensors
  const deviceParams = useMemo(() => {
    if (!selectedDevice) return []
    const dev = allDevices.find((d) => String(d.id) === String(selectedDevice))
    return deviceFlatParams(dev)
  }, [selectedDevice, allDevices])

  if (!open) return null

  const handleSave = () => {
    onSave({
      widgetName: widgetName.trim(),
      selectedDevice,
      selectedParameter,
      min:   minValue   === '' ? null : Number(minValue),
      max:   maxValue   === '' ? null : Number(maxValue),
      steps: stepsValue === '' ? null : Number(stepsValue),
    })
    onClose()
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header with Save / Cancel buttons on the right */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-gray-100">
          <h2 className="text-base font-semibold text-[#4D4D4D]">Gauge Series Editor</h2>
          <div className="flex items-center gap-2">
            <button onClick={handleSave} className="px-4 py-1.5 text-sm text-white bg-[#358ECD] rounded-lg hover:bg-[#2d7ab8] transition font-medium">Save</button>
            <button onClick={onClose} className="px-4 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition">Cancel</button>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
          {/* Widget Name */}
          <div>
            <label className="block text-xs font-semibold text-[#4D4D4D] mb-1">
              Widget Name
              <span className="ml-1 font-normal text-gray-400">(Leave Blank For Default)</span>
            </label>
            <input
              type="text" placeholder="Gauge Series"
              value={widgetName} onChange={(e) => setWidgetName(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD]"
            />
          </div>

          {/* Parameter Selection */}
          <div>
            <h3 className="text-xs font-semibold text-[#4D4D4D] mb-3">Parameter Selection</h3>

            <div className="mb-3">
              <label className="block text-xs text-gray-500 mb-1">Device</label>
              <select
                value={selectedDevice}
                onChange={(e) => { setSelectedDevice(e.target.value); setSelectedParameter('') }}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD]"
              >
                <option value="">{loading ? 'Loading devices…' : 'Select Device…'}</option>
                {allDevices.map((dev) => (
                  <option key={dev.id} value={dev.id}>
                    {dev.siteName ? `${dev.siteName} — ` : ''}{dev.name || dev.serial_number || `Device ${dev.id}`}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs text-gray-500 mb-1">Parameter</label>
              <select
                value={selectedParameter}
                onChange={(e) => setSelectedParameter(e.target.value)}
                disabled={!selectedDevice || deviceParams.length === 0}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD] disabled:bg-gray-50 disabled:text-gray-400"
              >
                <option value="">Select Parameter…</option>
                {deviceParams.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Range — Min / Max / Steps */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-semibold text-[#4D4D4D] mb-1">Min</label>
              <input
                type="number" inputMode="decimal" step="any"
                placeholder="Minimum #"
                value={minValue} onChange={(e) => setMinValue(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD]"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#4D4D4D] mb-1">Max</label>
              <input
                type="number" inputMode="decimal" step="any"
                placeholder="Maximum #"
                value={maxValue} onChange={(e) => setMaxValue(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD]"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#4D4D4D] mb-1">Steps</label>
              <input
                type="number" inputMode="numeric" step="1" min="1"
                placeholder="Default"
                value={stepsValue} onChange={(e) => setStepsValue(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD]"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Semicircle Gauge SVG ────────────────────────────────────────────────────
// Pure-SVG semicircle with three coloured arc segments (green / yellow / red)
// at fixed percentages of the range, evenly-spaced tick labels, and a needle
// pointing to the current value. Responsive — scales to fill container width
// up to `maxWidth`, never clips inside its card.
function SemicircleGauge({ value, min, max, steps, unit, maxWidth = 320 }) {
  const safeMin = Number.isFinite(min) ? min : 0
  const safeMax = Number.isFinite(max) && max > safeMin ? max : safeMin + 100
  const safeValue = value == null || !Number.isFinite(value)
    ? null
    : Math.max(safeMin, Math.min(safeMax, value))

  // Fixed internal coordinates — viewBox does the scaling.
  const w = 320
  const h = 220
  const cx = w / 2
  const cy = 150
  const r = 120
  const stroke = 18

  // Map a fraction (0..1) → angle on a half-arc going from -90° (left) to +90° (right)
  const fracToAngle = (f) => -90 + 180 * Math.max(0, Math.min(1, f))
  const deg2rad = (d) => (d * Math.PI) / 180

  const arc = (startFrac, endFrac, color) => {
    const a0 = deg2rad(fracToAngle(startFrac))
    const a1 = deg2rad(fracToAngle(endFrac))
    const x0 = cx + r * Math.cos(a0)
    const y0 = cy + r * Math.sin(a0)
    const x1 = cx + r * Math.cos(a1)
    const y1 = cy + r * Math.sin(a1)
    const large = endFrac - startFrac > 0.5 ? 1 : 0
    return (
      <path
        d={`M ${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1}`}
        stroke={color}
        strokeWidth={stroke}
        fill="none"
        strokeLinecap="butt"
      />
    )
  }

  // Coloured zones — fractions of the visible range. Matches the customer's
  // example screenshot: green to ~half, yellow short band, red to the end.
  const GREEN  = '#56B565'
  const YELLOW = '#F4D03F'
  const RED    = '#E36B6B'

  // Tick labels — `steps` is the number of intervals between ticks.
  // If unset, default to 8 (giving 9 labels at 0/25/50/.../200 for 0..200).
  const tickCount = Math.max(2, Math.round(Number(steps) || 8))
  const tickFracs = Array.from({ length: tickCount + 1 }, (_, i) => i / tickCount)

  // Needle
  const valueFrac = safeValue == null ? null : (safeValue - safeMin) / (safeMax - safeMin)
  const needleAngle = valueFrac == null ? null : deg2rad(fracToAngle(valueFrac))
  const needleR = r - stroke / 2 - 6
  const needleX = needleAngle == null ? cx : cx + needleR * Math.cos(needleAngle)
  const needleY = needleAngle == null ? cy : cy + needleR * Math.sin(needleAngle)

  // Format value: 1 decimal max, drop trailing zeros
  const formatVal = (v) => {
    if (v == null || !Number.isFinite(v)) return '—'
    const s = Math.abs(v) >= 100 ? v.toFixed(0) : v.toFixed(1)
    return s.replace(/\.0$/, '')
  }

  return (
    <div style={{ width: '100%', maxWidth, margin: '0 auto' }}>
      <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="xMidYMid meet" style={{ width: '100%', height: 'auto', display: 'block' }} aria-label="Gauge">
        {/* Light grey baseline arc behind everything */}
        {arc(0, 1, '#E5E7EB')}

        {/* Coloured zones layered on top */}
        {arc(0,    0.50, GREEN)}
        {arc(0.50, 0.625, YELLOW)}
        {arc(0.625, 1.00, RED)}

        {/* Tick labels */}
        {tickFracs.map((f, i) => {
          const a = deg2rad(fracToAngle(f))
          const tr = r - stroke - 14
          const tx = cx + tr * Math.cos(a)
          const ty = cy + tr * Math.sin(a)
          const labelValue = safeMin + f * (safeMax - safeMin)
          return (
            <text key={i} x={tx} y={ty}
                  textAnchor="middle" dominantBaseline="middle"
                  fontSize="11" fill="#6B7280" fontWeight="500">
              {formatVal(labelValue)}
            </text>
          )
        })}

        {/* Needle */}
        {needleAngle != null && (
          <>
            <line x1={cx} y1={cy} x2={needleX} y2={needleY}
                  stroke="#374151" strokeWidth="3" strokeLinecap="round" />
            <circle cx={cx} cy={cy} r="6" fill="#374151" />
          </>
        )}

        {/* Value + unit label below the gauge */}
        <text x={cx} y={cy + 36}
              textAnchor="middle" fontSize="15" fontWeight="700" fill="#1F2937">
          {formatVal(safeValue)}{unit ? ` ${unit}` : ''}
        </text>
      </svg>
    </div>
  )
}

/**
 * Gauge Series Widget — renders a semicircle dial for the latest value of
 * one configured parameter, with min/max/steps controlling the scale and
 * tick labels. Editor lives in `GaugeSeriesEditorModal`. Settings persist
 * in localStorage (project-scoped).
 */
export function GaugeSeriesWidget({ widget }) {
  const { projectId } = useParams()
  const { isCustomizing = false } = useDashboardContext()

  const [settings, setSettings] = useState(
    () => lsGetScoped('wq_gauge', projectId, widget.id, {})
  )
  const [showEditor, setShowEditor] = useState(false)
  const [latestValue, setLatestValue] = useState(null)
  const [unit, setUnit] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const handleSave = (next) => {
    setSettings(next)
    lsSetScoped('wq_gauge', projectId, widget.id, next)
  }

  const paramId = settings.selectedParameter
  const configured = Boolean(paramId)

  // Fetch latest value whenever the chosen parameter changes
  useEffect(() => {
    if (!configured) { setLatestValue(null); setUnit(''); return }
    let cancelled = false
    setLoading(true); setError(null)
    projectsApi.fetchGraphData(Number(paramId), {})
      .then((res) => {
        if (cancelled) return
        if (res && typeof res === 'object' && !res.error) {
          setLatestValue(res.latestValue ?? null)
          setUnit(res.unit || '')
        } else {
          setLatestValue(null)
          setUnit('')
        }
      })
      .catch((e) => { if (!cancelled) { console.error('[Gauge]', e); setError('Could not load value.') } })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [configured, paramId])

  return (
    <>
      <XDCard
        title={settings.widgetName || widget.title || 'Gauge Series'}
        subtitle={widget.description}
        headerRight={
          !isCustomizing ? (
            <button
              onClick={() => setShowEditor(true)}
              className="p-1 text-gray-400 hover:text-[#358ECD] rounded transition"
              title="Edit gauge"
            >
              <EditIcon />
            </button>
          ) : null
        }
      >
        <div className="flex flex-col items-center justify-center p-4 h-full">
          {!configured ? (
            <div className="flex flex-col items-center justify-center text-gray-400 gap-2 py-10">
              <span className="text-3xl">📍</span>
              <p className="text-sm text-center">Pick a device and parameter in the editor.</p>
            </div>
          ) : loading ? (
            <Loader className="w-5 h-5 animate-spin text-[#358ECD]" />
          ) : error ? (
            <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">{error}</div>
          ) : (
            <SemicircleGauge
              value={latestValue}
              min={settings.min ?? 0}
              max={settings.max ?? 100}
              steps={settings.steps}
              unit={unit}
            />
          )}
        </div>
      </XDCard>

      <GaugeSeriesEditorModal
        open={showEditor}
        onClose={() => setShowEditor(false)}
        onSave={handleSave}
        projectId={projectId}
        initialSettings={settings}
      />
    </>
  )
}

// ─── Bullet Graph Editor Modal ───────────────────────────────────────────────
// Multi-parameter editor — each row has Device + Parameter + Min/Max/Steps.
// "+ Add Parameter" appends another blank row. Delete (✕) per row.
function BulletGraphEditorModal({ open, onClose, onSave, projectId, initialSettings = {} }) {
  const [widgetName, setWidgetName] = useState('')
  const [sites, setSites] = useState([])
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open) return
    setWidgetName(initialSettings.widgetName || '')
    const initial = Array.isArray(initialSettings.parameters) && initialSettings.parameters.length
      ? initialSettings.parameters
      : [{ id: 'p' + Date.now(), selectedDevice: '', selectedParameter: '', min: '', max: '', steps: '' }]
    setRows(initial.map((r, i) => ({ ...r, id: r.id || `p${i}_${Date.now()}` })))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === 'Escape') onClose?.() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  useEffect(() => {
    if (!open || !projectId) return
    setLoading(true)
    projectsApi.fetchProjectSites(Number(projectId))
      .then((data) => setSites(Array.isArray(data) ? data : []))
      .catch(() => setSites([]))
      .finally(() => setLoading(false))
  }, [open, projectId])

  const allDevices = useMemo(() => {
    const devs = []
    sites.forEach((site) => {
      ;(site.devices || []).forEach((dev) => devs.push({ ...dev, siteName: site.name || '' }))
    })
    return devs
  }, [sites])

  const paramsForDevice = (deviceId) => {
    if (!deviceId) return []
    return deviceFlatParams(allDevices.find((d) => String(d.id) === String(deviceId)))
  }

  const updateRow = (id, field, value) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)))
  }
  const addRow = () => {
    setRows((prev) => [...prev, { id: 'p' + Date.now(), selectedDevice: '', selectedParameter: '', min: '', max: '', steps: '' }])
  }
  const removeRow = (id) => setRows((prev) => prev.filter((r) => r.id !== id))

  if (!open) return null

  const save = () => {
    const cleaned = rows
      .filter((r) => r.selectedDevice && r.selectedParameter)
      .map((r) => ({
        id: r.id,
        selectedDevice: r.selectedDevice,
        selectedParameter: r.selectedParameter,
        min:   r.min   === '' ? null : Number(r.min),
        max:   r.max   === '' ? null : Number(r.max),
        steps: r.steps === '' ? null : Number(r.steps),
      }))
    onSave({ widgetName: widgetName.trim(), parameters: cleaned })
    onClose()
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-3 border-b border-gray-100">
          <h2 className="text-base font-semibold text-[#4D4D4D]">Bullet Graph Editor</h2>
          <div className="flex items-center gap-2">
            <button onClick={save} className="px-4 py-1.5 text-sm text-white bg-[#358ECD] rounded-lg hover:bg-[#2d7ab8] transition font-medium">Save</button>
            <button onClick={onClose} className="px-4 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition">Cancel</button>
          </div>
        </div>
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
          <div>
            <label className="block text-xs font-semibold text-[#4D4D4D] mb-1">
              Widget Name <span className="ml-1 font-normal text-gray-400">(Leave Blank For Default)</span>
            </label>
            <input type="text" placeholder="Bullet Graph" value={widgetName} onChange={(e) => setWidgetName(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD]" />
          </div>

          <div>
            <h3 className="text-xs font-semibold text-[#4D4D4D] mb-3">Parameter Selection</h3>
            <div className="space-y-4">
              {rows.map((row, idx) => (
                <div key={row.id} className="space-y-2 border border-gray-100 rounded-lg p-3 bg-gray-50/40">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-semibold text-gray-500">Parameter {idx + 1}</span>
                    {rows.length > 1 && (
                      <button type="button" onClick={() => removeRow(row.id)} className="text-red-500 hover:text-red-700" title="Remove">
                        <X size={14} />
                      </button>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Device</label>
                    <select value={row.selectedDevice} onChange={(e) => { updateRow(row.id, 'selectedDevice', e.target.value); updateRow(row.id, 'selectedParameter', '') }}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD]">
                      <option value="">{loading ? 'Loading devices…' : 'Select Device'}</option>
                      {allDevices.map((dev) => (
                        <option key={dev.id} value={dev.id}>
                          {dev.siteName ? `${dev.siteName} — ` : ''}{dev.name || `Device ${dev.id}`}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Parameter</label>
                    <select value={row.selectedParameter} onChange={(e) => updateRow(row.id, 'selectedParameter', e.target.value)}
                      disabled={!row.selectedDevice}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD] disabled:bg-gray-50 disabled:text-gray-400">
                      <option value="">Select Parameter</option>
                      {paramsForDevice(row.selectedDevice).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <input type="number" inputMode="decimal" step="any" placeholder="Minimum #" value={row.min} onChange={(e) => updateRow(row.id, 'min', e.target.value)}
                      className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-[#358ECD]" />
                    <input type="number" inputMode="decimal" step="any" placeholder="Maximum #" value={row.max} onChange={(e) => updateRow(row.id, 'max', e.target.value)}
                      className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-[#358ECD]" />
                    <input type="number" inputMode="numeric" step="1" min="1" placeholder="Default" value={row.steps} onChange={(e) => updateRow(row.id, 'steps', e.target.value)}
                      className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-[#358ECD]" />
                  </div>
                </div>
              ))}
            </div>
            <button type="button" onClick={addRow}
              className="mt-3 text-sm font-medium text-[#358ECD] hover:underline flex items-center gap-1">
              <span className="text-base leading-none">+</span> Add Parameter
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Single horizontal bullet bar
function BulletBar({ label, sublabel, value, min, max, steps, color = '#F37D5C' }) {
  const lo = Number.isFinite(min) ? min : 0
  const hi = Number.isFinite(max) && max > lo ? max : lo + 100
  const v = value == null || !Number.isFinite(value) ? null : Math.max(lo, Math.min(hi, value))
  const pct = v == null ? 0 : ((v - lo) / (hi - lo)) * 100
  const tickCount = Math.max(2, Math.round(Number(steps) || 6))
  const ticks = Array.from({ length: tickCount + 1 }, (_, i) => lo + (i / tickCount) * (hi - lo))
  const fmt = (n) => Math.abs(n) >= 100 ? Math.round(n).toString() : (Math.round(n * 10) / 10).toString()

  return (
    <div className="grid grid-cols-[110px_1fr] items-center gap-3">
      <div className="text-right">
        <div className="text-[11px] font-semibold text-gray-800 leading-tight">{label}</div>
        {sublabel && <div className="text-[10px] text-gray-500">{sublabel}</div>}
      </div>
      <div>
        <div className="relative w-full h-5 bg-gray-100 rounded">
          {v != null && (
            <div className="absolute inset-y-0 left-0 rounded-l" style={{ width: `${pct}%`, backgroundColor: color, opacity: 0.85 }} />
          )}
          {/* "target" marker — value position drawn as a small dark notch */}
          {v != null && (
            <div className="absolute top-0 bottom-0 w-0.5 bg-gray-800" style={{ left: `calc(${pct}% - 1px)` }} />
          )}
        </div>
        <div className="relative w-full h-3 mt-0.5">
          {ticks.map((t, i) => {
            const left = (i / tickCount) * 100
            return (
              <span key={i}
                    className="absolute -translate-x-1/2 text-[9px] text-gray-500"
                    style={{ left: `${left}%` }}>
                {fmt(t)}
              </span>
            )
          })}
        </div>
      </div>
    </div>
  )
}

/**
 * Bullet Graph Widget — stacks one horizontal bullet bar per configured
 * parameter. Latest value drives the fill width and the dark notch.
 */
export function BulletGraphWidget({ widget }) {
  const { projectId } = useParams()
  const { isCustomizing = false } = useDashboardContext()
  const [settings, setSettings] = useState(() => lsGetScoped('wq_bullet', projectId, widget.id, {}))
  const [showEditor, setShowEditor] = useState(false)
  const [paramMeta, setParamMeta] = useState({})    // id → {name, unit_name}
  const [values, setValues] = useState({})          // id → {value, unit}

  const handleSave = (next) => {
    setSettings(next)
    lsSetScoped('wq_bullet', projectId, widget.id, next)
  }

  const params = Array.isArray(settings.parameters) ? settings.parameters : []

  // Resolve parameter names from project sites (so labels show the human name)
  useEffect(() => {
    if (!projectId || params.length === 0) { setParamMeta({}); return }
    let cancelled = false
    projectsApi.fetchProjectSites(Number(projectId))
      .then((sites) => {
        if (cancelled) return
        const out = {}
        for (const s of (sites || [])) {
          for (const d of (s.devices || [])) {
            for (const p of deviceFlatParams(d)) {
              out[String(p.id)] = { name: p.name || '', unit_name: p.unit || '' }
            }
          }
        }
        setParamMeta(out)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [projectId, params.length])

  // Fetch latest values
  useEffect(() => {
    if (params.length === 0) { setValues({}); return }
    let cancelled = false
    Promise.allSettled(
      params.map((p) =>
        projectsApi.fetchGraphData(Number(p.selectedParameter), {})
          .then((res) => ({
            id: String(p.selectedParameter),
            value: (res && typeof res === 'object' && !res.error) ? (res.latestValue ?? null) : null,
            unit:  (res && res.unit) || '',
          }))
      )
    ).then((results) => {
      if (cancelled) return
      const map = {}
      results.forEach((r) => { if (r.status === 'fulfilled' && r.value?.id) map[r.value.id] = r.value })
      setValues(map)
    })
    return () => { cancelled = true }
  }, [JSON.stringify(params.map((p) => p.selectedParameter))])

  return (
    <>
      <XDCard
        title={settings.widgetName || widget.title || 'Bullet Graph'}
        subtitle={widget.description}
        headerRight={!isCustomizing ? (
          <button onClick={() => setShowEditor(true)} className="p-1 text-gray-400 hover:text-[#358ECD] rounded transition" title="Edit bullet graph">
            <EditIcon />
          </button>
        ) : null}
      >
        <div className="p-4">
          {params.length === 0 ? (
            <div className="flex flex-col items-center justify-center text-gray-400 gap-2 py-10">
              <span className="text-3xl">🎯</span>
              <p className="text-sm text-center">Add a parameter in the editor to render a bar.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {params.map((p) => {
                const meta = paramMeta[String(p.selectedParameter)] || {}
                const v    = values[String(p.selectedParameter)]
                return (
                  <BulletBar
                    key={p.id}
                    label={meta.name || `Param ${p.selectedParameter}`}
                    sublabel={meta.unit_name || v?.unit || ''}
                    value={v?.value ?? null}
                    min={p.min ?? 0}
                    max={p.max ?? 100}
                    steps={p.steps}
                  />
                )
              })}
            </div>
          )}
        </div>
      </XDCard>
      <BulletGraphEditorModal open={showEditor} onClose={() => setShowEditor(false)} onSave={handleSave} projectId={projectId} initialSettings={settings} />
    </>
  )
}

// ─── Solid Gauge Editor Modal ────────────────────────────────────────────────
function SolidGaugeEditorModal({ open, onClose, onSave, projectId, initialSettings = {} }) {
  const [widgetName, setWidgetName] = useState('')
  const [sites, setSites] = useState([])
  const [selectedDevice, setSelectedDevice] = useState('')
  const [selectedParameter, setSelectedParameter] = useState('')
  const [minValue, setMinValue] = useState('')
  const [maxValue, setMaxValue] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open) return
    setWidgetName(initialSettings.widgetName || '')
    setSelectedDevice(initialSettings.selectedDevice || '')
    setSelectedParameter(initialSettings.selectedParameter || '')
    setMinValue(initialSettings.min ?? '')
    setMaxValue(initialSettings.max ?? '')
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === 'Escape') onClose?.() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  useEffect(() => {
    if (!open || !projectId) return
    setLoading(true)
    projectsApi.fetchProjectSites(Number(projectId))
      .then((data) => setSites(Array.isArray(data) ? data : []))
      .catch(() => setSites([]))
      .finally(() => setLoading(false))
  }, [open, projectId])

  const allDevices = useMemo(() => {
    const devs = []
    sites.forEach((site) => {
      ;(site.devices || []).forEach((dev) => devs.push({ ...dev, siteName: site.name || '' }))
    })
    return devs
  }, [sites])

  const deviceParams = useMemo(() => {
    if (!selectedDevice) return []
    return deviceFlatParams(allDevices.find((d) => String(d.id) === String(selectedDevice)))
  }, [selectedDevice, allDevices])

  if (!open) return null

  const save = () => {
    onSave({
      widgetName: widgetName.trim(),
      selectedDevice,
      selectedParameter,
      min: minValue === '' ? null : Number(minValue),
      max: maxValue === '' ? null : Number(maxValue),
    })
    onClose()
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-3 border-b border-gray-100">
          <h2 className="text-base font-semibold text-[#4D4D4D]">Solid Gauge Editor</h2>
          <div className="flex items-center gap-2">
            <button onClick={save} className="px-4 py-1.5 text-sm text-white bg-[#358ECD] rounded-lg hover:bg-[#2d7ab8] transition font-medium">Save</button>
            <button onClick={onClose} className="px-4 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition">Cancel</button>
          </div>
        </div>
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
          <div>
            <label className="block text-xs font-semibold text-[#4D4D4D] mb-1">
              Widget Name <span className="ml-1 font-normal text-gray-400">(Leave Blank For Default)</span>
            </label>
            <input type="text" placeholder="Solid Gauge" value={widgetName} onChange={(e) => setWidgetName(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD]" />
          </div>
          <div>
            <h3 className="text-xs font-semibold text-[#4D4D4D] mb-3">Parameter Selection</h3>
            <div className="mb-3">
              <label className="block text-xs text-gray-500 mb-1">Device</label>
              <select value={selectedDevice} onChange={(e) => { setSelectedDevice(e.target.value); setSelectedParameter('') }}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD]">
                <option value="">{loading ? 'Loading devices…' : 'Select Device…'}</option>
                {allDevices.map((dev) => (
                  <option key={dev.id} value={dev.id}>
                    {dev.siteName ? `${dev.siteName} — ` : ''}{dev.name || `Device ${dev.id}`}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Parameter</label>
              <select value={selectedParameter} onChange={(e) => setSelectedParameter(e.target.value)}
                disabled={!selectedDevice || deviceParams.length === 0}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD] disabled:bg-gray-50 disabled:text-gray-400">
                <option value="">Select Parameter…</option>
                {deviceParams.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-[#4D4D4D] mb-1">Min</label>
              <input type="number" inputMode="decimal" step="any" placeholder="Minimum #" value={minValue} onChange={(e) => setMinValue(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD]" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#4D4D4D] mb-1">Max</label>
              <input type="number" inputMode="decimal" step="any" placeholder="Maximum #" value={maxValue} onChange={(e) => setMaxValue(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD]" />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Solid-fill arc gauge — semicircle, single colour fill (no green/yellow/red zones),
// title above the arc, value+unit centred below. Renders RESPONSIVELY — the SVG
// scales to fill its container width up to a max, so it never clips inside its card.
function SolidGaugeArc({ value, min, max, unit, label, maxWidth = 280 }) {
  const lo = Number.isFinite(min) ? min : 0
  const hi = Number.isFinite(max) && max > lo ? max : lo + 100
  const v  = value == null || !Number.isFinite(value) ? null : Math.max(lo, Math.min(hi, value))

  // Fixed internal coordinates — the viewBox does the scaling.
  const W  = 260
  const H  = 180
  const cx = W / 2
  const cy = 130    // arc baseline ~halfway down so we leave room for label + value
  const r  = 90
  const stroke = 22
  const FILL = '#C9D643'  // muted lime green from the XD

  const fracToAngle = (f) => -90 + 180 * Math.max(0, Math.min(1, f))
  const d2r = (d) => (d * Math.PI) / 180
  const arc = (f0, f1, color) => {
    const a0 = d2r(fracToAngle(f0))
    const a1 = d2r(fracToAngle(f1))
    const x0 = cx + r * Math.cos(a0), y0 = cy + r * Math.sin(a0)
    const x1 = cx + r * Math.cos(a1), y1 = cy + r * Math.sin(a1)
    const large = f1 - f0 > 0.5 ? 1 : 0
    return <path d={`M ${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1}`} stroke={color} strokeWidth={stroke} fill="none" strokeLinecap="butt" />
  }

  const frac = v == null ? 0 : (v - lo) / (hi - lo)
  const fmt = (n) => {
    if (n == null || !Number.isFinite(n)) return '—'
    const s = Math.abs(n) >= 100 ? n.toFixed(0) : n.toFixed(1)
    return s.replace(/\.0$/, '')
  }

  return (
    <div style={{ width: '100%', maxWidth, margin: '0 auto' }}>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" style={{ width: '100%', height: 'auto', display: 'block' }} aria-label="Solid gauge">
        {label && (
          <text x={cx} y={cy - r - 8} textAnchor="middle" fontSize="13" fill="#6B7280">{label}</text>
        )}
        {arc(0, 1, '#E5E7EB')}
        {v != null && frac > 0 && arc(0, frac, FILL)}
        {/* Tick labels at the ends of the arc */}
        <text x={cx - r} y={cy + 18} textAnchor="middle" fontSize="11" fill="#9CA3AF">{fmt(lo)}</text>
        <text x={cx + r} y={cy + 18} textAnchor="middle" fontSize="11" fill="#9CA3AF">{fmt(hi)}</text>
        {/* Big value below the arc */}
        <text x={cx} y={cy + 4} textAnchor="middle" fontSize="26" fontWeight="700" fill="#1F2937">{fmt(v)}</text>
        {unit && <text x={cx} y={cy + 22} textAnchor="middle" fontSize="11" fill="#6B7280">{unit}</text>}
      </svg>
    </div>
  )
}

/**
 * Solid Gauge Widget — single-arc fill gauge for one parameter's latest value.
 */
export function SolidGaugeWidget({ widget }) {
  const { projectId } = useParams()
  const { isCustomizing = false } = useDashboardContext()
  const [settings, setSettings] = useState(() => lsGetScoped('wq_solid', projectId, widget.id, {}))
  const [showEditor, setShowEditor] = useState(false)
  const [latestValue, setLatestValue] = useState(null)
  const [unit, setUnit] = useState('')
  const [paramName, setParamName] = useState('')
  const [loading, setLoading] = useState(false)

  const handleSave = (next) => {
    setSettings(next)
    lsSetScoped('wq_solid', projectId, widget.id, next)
  }

  const paramId = settings.selectedParameter
  const configured = Boolean(paramId)

  // Resolve parameter name from cached sites for the "Speed" label above the gauge
  useEffect(() => {
    if (!configured || !projectId) { setParamName(''); return }
    let cancelled = false
    projectsApi.fetchProjectSites(Number(projectId))
      .then((sites) => {
        if (cancelled) return
        for (const s of (sites || [])) {
          for (const d of (s.devices || [])) {
            const p = deviceFlatParams(d).find((x) => String(x.id) === String(paramId))
            if (p) { setParamName(p.name || ''); return }
          }
        }
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [configured, paramId, projectId])

  useEffect(() => {
    if (!configured) { setLatestValue(null); setUnit(''); return }
    let cancelled = false
    setLoading(true)
    projectsApi.fetchGraphData(Number(paramId), {})
      .then((res) => {
        if (cancelled) return
        if (res && typeof res === 'object' && !res.error) {
          setLatestValue(res.latestValue ?? null)
          setUnit(res.unit || '')
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [configured, paramId])

  return (
    <>
      <XDCard
        title={settings.widgetName || widget.title || 'Solid Gauge'}
        subtitle={widget.description}
        headerRight={!isCustomizing ? (
          <button onClick={() => setShowEditor(true)} className="p-1 text-gray-400 hover:text-[#358ECD] rounded transition" title="Edit gauge">
            <EditIcon />
          </button>
        ) : null}
      >
        <div className="flex flex-col items-center justify-center p-4 h-full">
          {!configured ? (
            <div className="flex flex-col items-center justify-center text-gray-400 gap-2 py-10">
              <span className="text-3xl">📍</span>
              <p className="text-sm text-center">Pick a device and parameter in the editor.</p>
            </div>
          ) : loading ? (
            <Loader className="w-5 h-5 animate-spin text-[#358ECD]" />
          ) : (
            <SolidGaugeArc value={latestValue} min={settings.min ?? 0} max={settings.max ?? 100} unit={unit} label={paramName} />
          )}
        </div>
      </XDCard>
      <SolidGaugeEditorModal open={showEditor} onClose={() => setShowEditor(false)} onSave={handleSave} projectId={projectId} initialSettings={settings} />
    </>
  )
}

// ============= WEATHER PANEL WIDGETS =============

/**
 * Generic Weather/Water/Soil Panel Widget
 */
function PanelWidget({ widget, data, bgColor = 'from-blue-50 to-blue-100', borderColor = 'border-blue-200', valueColor = 'text-blue-600', icon = '📊' }) {
  return (
    <div className={`bg-white rounded-lg shadow-sm h-full flex flex-col overflow-hidden border-l-4 ${borderColor.replace('border-', 'border-l-').split(' ')[0]}`}>
      <div className="flex border-b border-gray-100 items-center justify-between px-4 py-3 shrink-0">
        <div>
          <h3 className="font-bold text-[#4D4D4D] text-base">{widget.title}</h3>
          {widget.description && <p className="text-xs text-gray-400 mt-0.5 leading-tight">{widget.description}</p>}
        </div>
        <span className="text-2xl">{icon}</span>
      </div>
      <div className={`flex-1 flex items-center px-6 py-4 bg-gradient-to-br ${bgColor}`}>
        <div>
          <div className={`text-5xl font-bold ${valueColor}`}>{data?.value ?? '—'}</div>
          {data?.unit && <p className="text-sm text-gray-600 mt-1">{data.unit}</p>}
          {data?.change != null && (
            <p className="text-sm text-gray-600 mt-1">{data.change > 0 ? '↑' : '↓'} {Math.abs(data.change)}</p>
          )}
          {!data?.value && (
            <p className="text-sm text-gray-400 mt-1">No live data</p>
          )}
        </div>
      </div>
    </div>
  )
}

export function AirTemperatureWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-orange-50 to-orange-100" borderColor="border-orange-200" valueColor="text-orange-600" icon="🌡️" />
}

export function BarometricPressureWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-purple-50 to-purple-100" borderColor="border-purple-200" valueColor="text-purple-600" icon="🌪️" />
}

export function DailyRainWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-cyan-50 to-cyan-100" borderColor="border-cyan-200" valueColor="text-cyan-600" icon="🌧️" />
}

export function HeadingWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-indigo-50 to-indigo-100" borderColor="border-indigo-200" valueColor="text-indigo-600" icon="🧭" />
}

export function HumidityWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-blue-50 to-blue-100" borderColor="border-blue-200" valueColor="text-blue-600" icon="💧" />
}

export function IntervalRainWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-cyan-50 to-cyan-100" borderColor="border-cyan-200" valueColor="text-cyan-600" icon="🌧️" />
}

export function LightningWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-yellow-50 to-yellow-100" borderColor="border-yellow-200" valueColor="text-yellow-600" icon="⚡" />
}

export function PrecipitationWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-blue-50 to-blue-100" borderColor="border-blue-200" valueColor="text-blue-600" icon="🌧️" />
}

export function PressureWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-purple-50 to-purple-100" borderColor="border-purple-200" valueColor="text-purple-600" icon="🌪️" />
}

export function RainIntensityWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-blue-50 to-blue-100" borderColor="border-blue-200" valueColor="text-blue-600" icon="🌧️" />
}

export function SolarRadiationWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-yellow-50 to-yellow-100" borderColor="border-yellow-200" valueColor="text-yellow-600" icon="☀️" />
}

export function TotalRainWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-cyan-50 to-cyan-100" borderColor="border-cyan-200" valueColor="text-cyan-600" icon="🌧️" />
}

export function WindWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-gray-50 to-gray-100" borderColor="border-gray-200" valueColor="text-gray-600" icon="💨" />
}

// ============= WATER PANEL WIDGETS =============

export function AmmoniumWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-blue-50 to-blue-100" borderColor="border-blue-200" valueColor="text-blue-600" icon="💧" />
}

export function BGAWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-green-50 to-green-100" borderColor="border-green-200" valueColor="text-green-600" icon="🌿" />
}

export function ChlorophyllWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-green-50 to-green-100" borderColor="border-green-200" valueColor="text-green-600" icon="🌿" />
}

export function ConductivityWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-yellow-50 to-yellow-100" borderColor="border-yellow-200" valueColor="text-yellow-600" icon="⚡" />
}

export function DissolvedOxygenWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-blue-50 to-blue-100" borderColor="border-blue-200" valueColor="text-blue-600" icon="💧" />
}

export function HydrocarbonsWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-orange-50 to-orange-100" borderColor="border-orange-200" valueColor="text-orange-600" icon="🛢️" />
}

export function LevelWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-blue-50 to-blue-100" borderColor="border-blue-200" valueColor="text-blue-600" icon="📏" />
}

export function OilDetectionWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-red-50 to-red-100" borderColor="border-red-200" valueColor="text-red-600" icon="🛢️" />
}

export function ORPWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-purple-50 to-purple-100" borderColor="border-purple-200" valueColor="text-purple-600" icon="⚡" />
}

export function PHWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-green-50 to-green-100" borderColor="border-green-200" valueColor="text-green-600" icon="⚗️" />
}

export function WaterPressureWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-blue-50 to-blue-100" borderColor="border-blue-200" valueColor="text-blue-600" icon="📊" />
}

export function SalinityWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-blue-50 to-blue-100" borderColor="border-blue-200" valueColor="text-blue-600" icon="🧂" />
}

export function SnowWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-blue-50 to-blue-100" borderColor="border-blue-200" valueColor="text-blue-600" icon="❄️" />
}

export function SpConductanceWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-yellow-50 to-yellow-100" borderColor="border-yellow-200" valueColor="text-yellow-600" icon="⚡" />
}

export function WaterTemperatureWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-red-50 to-red-100" borderColor="border-red-200" valueColor="text-red-600" icon="🌡️" />
}

export function TurbidityWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-gray-50 to-gray-100" borderColor="border-gray-200" valueColor="text-gray-600" icon="🌫️" />
}

export function WaterCurrentWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-blue-50 to-blue-100" borderColor="border-blue-200" valueColor="text-blue-600" icon="〰️" />
}

export function WaveWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-blue-50 to-blue-100" borderColor="border-blue-200" valueColor="text-blue-600" icon="〰️" />
}

// ============= SOIL PANEL WIDGETS =============

export function SoilConductivityWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-yellow-50 to-yellow-100" borderColor="border-yellow-200" valueColor="text-yellow-600" icon="⚡" />
}

export function SoilMoistureWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-green-50 to-green-100" borderColor="border-green-200" valueColor="text-green-600" icon="💧" />
}

export function SoilSalinityWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-yellow-50 to-yellow-100" borderColor="border-yellow-200" valueColor="text-yellow-600" icon="🧂" />
}

export function SoilTemperatureWidget({ widget, data }) {
  return <PanelWidget widget={widget} data={data} bgColor="from-orange-50 to-orange-100" borderColor="border-orange-200" valueColor="text-orange-600" icon="🌡️" />
}

// ============= OTHER WIDGETS =============

/**
 * GPS Widget — Project Map renderer.
 *
 * On the Project Map dashboard the backend template assigns this single
 * widget; the widget renders the full interactive Leaflet map plus the
 * top-right Edit button that opens the Map Editor popup (pin colours per
 * site type, base tile layer, default centre + zoom).
 *
 * The map needs the full project sites list and the active project id to
 * persist its per-project config — both come from DashboardContext so we
 * don't have to thread them through the widget registry.
 *
 * (Earlier this widget was a tiny "Location Coordinates" stub; the user
 * asked for the Project Map dashboard to render its assigned GPS widget
 * as the actual interactive map, with all map configuration living
 * inside the widget rather than at the page level.)
 */
export function GPSWidget({ widget }) {
  const { sites = [], projectId } = useDashboardContext()
  return (
    <Card className="p-0 overflow-hidden">
      {widget?.title && (
        <h3 className="text-lg font-semibold text-gray-800 px-4 pt-3 pb-2">
          {widget.title}
        </h3>
      )}
      <div className="p-3 pt-0">
        <ProjectMapView sites={sites} projectId={projectId} />
      </div>
    </Card>
  )
}

// ============= ALARM WIDGETS =============

/**
 * Alarm Summary Widget
 */
export function AlarmSummaryWidget({ widget, data }) {
  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">{widget.title}</h3>
      <div className="space-y-3">
        <div className="flex justify-between p-3 bg-red-50 rounded-lg border border-red-200">
          <span className="font-semibold text-red-700">Critical</span>
          <span className="font-bold text-lg text-red-600">{data?.critical || 0}</span>
        </div>
        <div className="flex justify-between p-3 bg-yellow-50 rounded-lg border border-yellow-200">
          <span className="font-semibold text-yellow-700">Warning</span>
          <span className="font-bold text-lg text-yellow-600">{data?.warning || 0}</span>
        </div>
        <div className="flex justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
          <span className="font-semibold text-blue-700">Info</span>
          <span className="font-bold text-lg text-blue-600">{data?.info || 0}</span>
        </div>
      </div>
    </Card>
  )
}

/**
 * Individual Alarm Widget
 */
export function IndividualAlarmWidget({ widget, data }) {
  const getSeverityStyles = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical':
        return 'bg-red-50 border-red-200 text-red-700'
      case 'warning':
        return 'bg-yellow-50 border-yellow-200 text-yellow-700'
      case 'info':
        return 'bg-blue-50 border-blue-200 text-blue-700'
      default:
        return 'bg-gray-50 border-gray-200 text-gray-700'
    }
  }

  return (
    <Card className={`p-6 border ${getSeverityStyles(data?.severity)}`}>
      <h3 className="text-lg font-semibold mb-2">{data?.name || widget.title}</h3>
      <p className="text-sm mb-3">{data?.message || 'No message'}</p>
      <div className="text-xs space-y-1">
        <p><span className="font-semibold">Status:</span> {data?.status || 'Active'}</p>
        <p><span className="font-semibold">Time:</span> {data?.timestamp || '—'}</p>
      </div>
    </Card>
  )
}

// ============= MISCELLANEOUS WIDGETS =============

/**
 * Project Description Widget
 */
export function ProjectDescriptionWidget({ widget, data, onConfigure }) {
  const { isCustomizing = false } = useDashboardContext()
  const html = widget.rich_content || ''
  const fallbackText = data?.description || ''
  return (
    <XDCard
      title={widget.title}
      subtitle={widget.description}
      headerRight={
        !isCustomizing && onConfigure ? (
          <button
            onClick={() => onConfigure(widget)}
            className="p-1 text-gray-400 hover:text-[#358ECD] rounded transition"
            title="Edit project description"
          >
            <EditIcon />
          </button>
        ) : null
      }
    >
      <div className="p-6 text-gray-600 text-base leading-relaxed overflow-auto">
        {html ? (
          <div
            className="prose prose-sm max-w-none rich-content-render"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        ) : (
          <p className="text-gray-400 italic text-center py-8">
            {fallbackText || 'No description yet · click the ✎ icon to add project description'}
          </p>
        )}
      </div>
    </XDCard>
  )
}

/**
 * Project Photo Widget — with edit icon, photo-change modal, and localStorage persistence
 */
export function ProjectPhotoWidget({ widget, data }) {
  const { projectId } = useParams()
  const { isCustomizing = false, goToGallery } = useDashboardContext()
  const [showPhotoModal, setShowPhotoModal] = useState(false)
  // Project-scoped key — see scopedLsKey doc-block. Without scoping, two
  // projects on the same Overview template would render the same custom
  // project photo because widget.id collides across projects.
  const [photoUrl, setPhotoUrl] = useState(
    () => lsGetScoped('wq_photo', projectId, widget.id, null) || data?.url || '/images/projectphoto.jpg'
  )

  // If the API provides a URL and we have no locally saved photo, use it
  useEffect(() => {
    if (data?.url && !lsGetScoped('wq_photo', projectId, widget.id, null)) setPhotoUrl(data.url)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.url, projectId])

  const handleSavePhoto = async (file) => {
    if (!file) return
    const reader = new FileReader()
    reader.onload = e => {
      const dataUrl = e.target.result
      setPhotoUrl(dataUrl)
      // Persist to localStorage; silently ignore quota errors
      lsSetScoped('wq_photo', projectId, widget.id, dataUrl)
    }
    reader.readAsDataURL(file)
  }

  return (
    <>
      <XDCard
        title={widget.title}
        subtitle={widget.description}
        headerRight={
          <div className="flex items-center gap-2">
            {/* Edit icon hidden in Customize mode */}
            {!isCustomizing && (
              <button
                onClick={() => setShowPhotoModal(true)}
                className="p-1 text-gray-400 hover:text-[#358ECD] rounded transition"
                title="Change project photo"
              >
                <EditIcon />
              </button>
            )}
            <button
              type="button"
              onClick={() => goToGallery?.()}
              disabled={isCustomizing}
              className="text-[#4D4D4D] flex items-center gap-2 font-medium text-sm bg-gray-200 py-1.5 px-4 rounded-lg hover:bg-gray-300 transition disabled:opacity-50 disabled:cursor-not-allowed"
              title="Open Gallery"
            >
              <ExternalLinkIcon /> View All
            </button>
          </div>
        }
      >
        <div className="flex items-center justify-center overflow-hidden p-6">
          <img src={photoUrl} alt={widget.title} className="max-w-full max-h-72 object-contain rounded" />
        </div>
      </XDCard>

      <ChangeProjectPhotoModal
        open={showPhotoModal}
        onClose={() => setShowPhotoModal(false)}
        onSave={handleSavePhoto}
        currentUrl={photoUrl}
      />
    </>
  )
}

/**
 * Gallery Widget — single-image carousel matching the XD design.
 *
 *   - One image shown at a time (full width of the widget body)
 *   - Caption (when set) sits under the image
 *   - "View All" button (top right) opens the Gallery tab via DashboardContext
 *   - Edit pencil opens GalleryEditorModal (add by URL or upload, caption,
 *     drag-reorder, remove)
 *   - `<` `>` arrows in the bottom-right corner cycle through images
 *   - Persists to localStorage `wq_gallery_${widget.id}`. Backward compatible
 *     with `widget.rich_content` JSON (legacy) and `data.images` (API).
 */
function parseGalleryImages(raw) {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : null
  } catch { return null }
}

// Normalise different image-source shapes to `{ id, src, caption }`.
function normaliseImage(img, idx) {
  const src = img?.src || img?.url || img
  return {
    id: img?.id || `g${idx}_${typeof src === 'string' ? src.slice(0, 20) : idx}`,
    src,
    caption: img?.caption || img?.alt || '',
  }
}

export function GalleryWidget({ widget, data }) {
  const { projectId } = useParams()
  const { isCustomizing = false, goToGallery } = useDashboardContext()

  // Persisted config: { albumId, albumName }. Picked via the "Change Gallery"
  // editor — the user selects one of the project's albums and the widget
  // shows its photos as a single-image carousel.
  //
  // KEY MUST BE PROJECT-SCOPED — every project using the Overview template
  // shares the same widget.id (1..15 from the seed), so an un-scoped key
  // would leak one project's selected album to all other projects.
  const lsConfig = lsGetScoped('wq_gallery', projectId, widget.id, null)
  const [albumConfig, setAlbumConfig] = useState(
    lsConfig && lsConfig.albumId
      ? { albumId: lsConfig.albumId, albumName: lsConfig.albumName || '' }
      : null
  )
  const [photos, setPhotos] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [index, setIndex] = useState(0)
  const [showEditor, setShowEditor] = useState(false)

  // Fetch photos whenever the selected album changes
  useEffect(() => {
    if (!albumConfig?.albumId) { setPhotos([]); return }
    let cancelled = false
    setLoading(true); setError(null)
    const API_URL = import.meta.env.VITE_API_URL || ''
    const JWT_KEY = import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token'
    const token = localStorage.getItem(JWT_KEY)
    fetch(`${API_URL}/api/photo/photo/?album=${albumConfig.albumId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then((res) => {
        if (cancelled) return
        const list = Array.isArray(res) ? res : (res.results || [])
        setPhotos(list)
        setIndex(0)
      })
      .catch((e) => { if (!cancelled) { console.error('[GalleryWidget]', e); setError('Could not load album photos.') } })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [albumConfig?.albumId])

  // Legacy fallback: if no album selected but we have inline images from
  // either widget.rich_content or `data`, display those. Keeps existing
  // dashboards working until the user re-picks an album.
  const legacyImages = useMemo(() => {
    if (albumConfig?.albumId) return []
    const rich = parseGalleryImages(widget.rich_content) || []
    if (rich.length > 0) return rich.map(normaliseImage)
    return (data?.images || []).map(normaliseImage)
  }, [albumConfig?.albumId, widget.rich_content, data?.images])

  const carouselItems = albumConfig?.albumId
    ? photos.map((p, i) => ({
        id: p.id,
        src: p.image || p.photo_url || p.url || p.file_url || p.thumbnail || '',
        caption: p.name || p.title || `Photo ${i + 1}`,
      })).filter((p) => p.src)
    : legacyImages

  const handleSave = (config) => {
    setAlbumConfig(config)
    setIndex(0)
    lsSetScoped('wq_gallery', projectId, widget.id, config)
  }

  const prev = () => setIndex((i) => (carouselItems.length ? (i - 1 + carouselItems.length) % carouselItems.length : 0))
  const next = () => setIndex((i) => (carouselItems.length ? (i + 1) % carouselItems.length : 0))

  const current = carouselItems[index]

  return (
    <>
      <XDCard
        title={widget.title || 'Gallery'}
        subtitle={widget.description}
        headerRight={
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => goToGallery?.(albumConfig?.albumName || undefined)}
              disabled={isCustomizing}
              className="text-[#4D4D4D] flex items-center gap-2 font-medium text-sm bg-gray-200 py-1.5 px-4 rounded-lg hover:bg-gray-300 transition disabled:opacity-50 disabled:cursor-not-allowed"
              title={albumConfig?.albumName ? `Open album: ${albumConfig.albumName}` : 'Open Gallery'}
            >
              <ExternalLinkIcon /> View All
            </button>
            {!isCustomizing && (
              <button
                onClick={() => setShowEditor(true)}
                className="p-1 text-gray-400 hover:text-[#358ECD] rounded transition"
                title="Change gallery album"
              >
                <EditIcon />
              </button>
            )}
          </div>
        }
      >
        <div className="relative h-full p-4 flex flex-col">
          {loading ? (
            <div className="flex items-center justify-center flex-1">
              <Loader className="w-5 h-5 animate-spin text-[#358ECD]" />
            </div>
          ) : error ? (
            <div className="flex items-center justify-center flex-1 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              {error}
            </div>
          ) : carouselItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center flex-1 text-gray-400 gap-2">
              <span className="text-3xl">🖼️</span>
              <p className="text-sm">
                {albumConfig?.albumId ? 'This album is empty.' : 'No album selected'}
              </p>
              <p className="text-xs text-gray-300">Click the edit (✎) icon to pick an album</p>
            </div>
          ) : (
            <>
              <div className="flex-1 flex items-center justify-center bg-white rounded overflow-hidden">
                <img
                  src={current.src}
                  alt={current.caption || `Image ${index + 1}`}
                  className="max-h-72 max-w-full object-contain"
                  onError={(e) => { e.target.style.opacity = 0.3 }}
                />
              </div>
              {current.caption && (
                <p className="text-xs text-center text-gray-600 mt-2 px-2 truncate">{current.caption}</p>
              )}

              {/* Prev / next arrows (bottom-right corner per XD) */}
              {carouselItems.length > 1 && (
                <div className="absolute bottom-3 right-3 flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={prev}
                    className="w-8 h-8 flex items-center justify-center bg-white border border-gray-200 rounded shadow-sm text-gray-700 hover:bg-gray-50"
                    title="Previous image"
                    aria-label="Previous image"
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    onClick={next}
                    className="w-8 h-8 flex items-center justify-center bg-white border border-gray-200 rounded shadow-sm text-gray-700 hover:bg-gray-50"
                    title="Next image"
                    aria-label="Next image"
                  >
                    ›
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </XDCard>

      <GalleryEditorModal
        open={showEditor}
        onClose={() => setShowEditor(false)}
        onSave={handleSave}
        projectId={projectId}
        initialAlbumId={albumConfig?.albumId || null}
      />
    </>
  )
}

// ─── "Change Webcam" modal — same flow as the Gallery's album picker ─────────
function ChangeWebcamModal({ open, onClose, onSave, projectId, initialWebcamId = null }) {
  const [webcams, setWebcams] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [selectedId, setSelectedId] = useState(initialWebcamId)

  useEffect(() => {
    if (!open) return
    setSelectedId(initialWebcamId)
    if (!projectId) { setWebcams([]); return }
    setLoading(true); setError(null)
    const API_URL = import.meta.env.VITE_API_URL || ''
    const JWT_KEY = import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token'
    const token = localStorage.getItem(JWT_KEY)
    fetch(`${API_URL}/api/data/camera/?site__project=${projectId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then((data) => {
        const list = Array.isArray(data) ? data : (data.results || [])
        setWebcams(list)
      })
      .catch((e) => { console.error('[Webcam] list:', e); setError('Could not load webcams.') })
      .finally(() => setLoading(false))
  }, [open, projectId, initialWebcamId])

  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === 'Escape') onClose?.() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  const camLabel = (cam) => {
    const folder = cam.ftp_folder || `Camera ${cam.id}`
    return folder
  }

  const handleSelect = () => {
    const cam = webcams.find((c) => String(c.id) === String(selectedId))
    if (!cam) return
    onSave({ webcamId: cam.id, webcamLabel: camLabel(cam), ftpFolder: cam.ftp_folder || '' })
    onClose()
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-[#4D4D4D]">Change Webcam</h2>
        </div>
        <div className="px-6 py-5 space-y-3">
          {error && <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded px-3 py-2">{error}</p>}
          <select
            value={selectedId || ''}
            onChange={(e) => setSelectedId(e.target.value ? Number(e.target.value) : null)}
            disabled={loading || webcams.length === 0}
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#358ECD] disabled:bg-gray-50 disabled:text-gray-400"
          >
            <option value="">
              {loading ? 'Loading webcams…' : (webcams.length === 0 ? 'No webcams available' : 'Select Webcam')}
            </option>
            {webcams.map((c) => (
              <option key={c.id} value={c.id}>{camLabel(c)}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={handleSelect}
            disabled={!selectedId || loading}
            className="w-full bg-[#358ECD] text-white font-semibold uppercase text-sm tracking-wide py-2.5 rounded-lg hover:bg-[#2d7ab8] disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            Select Webcam
          </button>
          <div className="text-center">
            <button type="button" onClick={onClose} className="text-sm text-[#358ECD] hover:underline">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Webcam Widget — picks one camera from the project's sites and displays
 * its feed. Configuration uses the "Change Webcam" modal pattern matching
 * the Gallery widget's "Change Gallery" picker.
 *
 * Note: the Camera model carries site + ftp_folder but no streaming URL —
 * actual feed rendering is best-effort (uses any `data.url` provided by
 * the surrounding widget data, otherwise shows a "Feed unavailable"
 * placeholder labelled with the chosen camera's ftp_folder).
 */
export function WebcamWidget({ widget, data }) {
  const { projectId } = useParams()
  const { isCustomizing = false, goToGallery } = useDashboardContext()
  const lsConfig = lsGetScoped('wq_webcam', projectId, widget.id, null)
  const [config, setConfig] = useState(
    lsConfig && lsConfig.webcamId
      ? { webcamId: lsConfig.webcamId, webcamLabel: lsConfig.webcamLabel || '', ftpFolder: lsConfig.ftpFolder || '' }
      : null
  )
  const [showEditor, setShowEditor] = useState(false)

  const handleSave = (next) => {
    setConfig(next)
    lsSetScoped('wq_webcam', projectId, widget.id, next)
  }

  return (
    <>
      <XDCard
        title={widget.title || 'Webcam'}
        subtitle={widget.description}
        headerRight={
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => goToGallery?.()}
              disabled={isCustomizing}
              className="text-[#4D4D4D] flex items-center gap-2 font-medium text-xs bg-gray-200 py-1.5 px-3 rounded-lg hover:bg-gray-300 transition disabled:opacity-50 disabled:cursor-not-allowed"
              title="Open Gallery"
            >
              <ExternalLinkIcon /> View All
            </button>
            {!isCustomizing && (
              <button
                onClick={() => setShowEditor(true)}
                className="p-1 text-gray-400 hover:text-[#358ECD] rounded transition"
                title="Change webcam"
              >
                <EditIcon />
              </button>
            )}
          </div>
        }
      >
        <div className="p-4">
          <div className="bg-black rounded-lg overflow-hidden flex items-center justify-center" style={{ minHeight: 200 }}>
            {data?.url ? (
              <img src={data.url} alt={config?.webcamLabel || 'Webcam feed'} className="w-full h-full object-cover" />
            ) : (
              <div className="text-center px-4 py-10">
                <p className="text-gray-300 text-sm">📹 {config ? `${config.webcamLabel || 'Webcam'} — feed unavailable` : 'Webcam feed unavailable'}</p>
                {!config && (
                  <p className="text-gray-500 text-xs mt-2">Click ✎ to pick a webcam</p>
                )}
              </div>
            )}
          </div>
        </div>
      </XDCard>

      <ChangeWebcamModal
        open={showEditor}
        onClose={() => setShowEditor(false)}
        onSave={handleSave}
        projectId={projectId}
        initialWebcamId={config?.webcamId || null}
      />
    </>
  )
}

// ─── Field Notes — helpers + popups ────────────────────────────────────────
// Note shape: { id, author, timestamp, body, attachmentDataUrl?, attachmentName? }
// Persisted per-project (and per-widget instance) via lsGetScoped/lsSetScoped.
// Attachments are stored as base64 data URLs in localStorage — fine for the
// handful of small site photos this widget targets; a real backend store
// will replace this when the persistence layer is built.

function formatFieldNoteTs(ts) {
  const d = new Date(ts)
  if (Number.isNaN(d.getTime())) return ''
  const date = d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
  // "8:00am EST" style — matches the XD which uses lowercase am/pm + zone.
  const tParts = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
  // toLocaleTimeString gives "8:00 AM"; collapse the space and lowercase the meridiem.
  const time = tParts.replace(/\s/g, '').replace('AM', 'am').replace('PM', 'pm')
  // Pull the timezone abbreviation off a separate formatToParts call (not all
  // browsers give it back from toLocaleTimeString reliably).
  let tz = ''
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' }).formatToParts(d)
    tz = parts.find((p) => p.type === 'timeZoneName')?.value || ''
  } catch { /* tz lookup is best-effort */ }
  return `${date} ${time}${tz ? ' ' + tz : ''}`
}

// Tiny paperclip icon — Tailwind sized via parent className.
const PaperclipIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 17.93 8.83l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
  </svg>
)

/**
 * "New Field Note" popup — title, Save/Cancel header buttons, large
 * textarea with the current timestamp prefilled at the top, and a
 * "Drag Attachments or Upload Image" drop zone at the bottom.
 *
 * Returns the new note via onSave; parent persists.
 */
function FieldNotesNewModal({ open, onSave, onCancel, defaultAuthor }) {
  const [body, setBody] = useState('')
  const [attachment, setAttachment] = useState(null) // { dataUrl, name } | null
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef(null)
  const ts = useMemo(() => new Date().toISOString(), [open])

  useEffect(() => {
    if (open) { setBody(''); setAttachment(null); setDragOver(false) }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === 'Escape') onCancel() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onCancel])

  if (!open) return null

  const ingestFile = (file) => {
    if (!file || !file.type?.startsWith('image/')) return
    // 5 MB ceiling so a single attachment can't blow out the localStorage
    // quota — anything larger gets rejected with a toast-style inline note.
    if (file.size > 5 * 1024 * 1024) {
      alert('Attachment too large (max 5 MB).')
      return
    }
    const reader = new FileReader()
    reader.onload = () => setAttachment({ dataUrl: String(reader.result), name: file.name })
    reader.readAsDataURL(file)
  }

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false)
    const f = e.dataTransfer?.files?.[0]
    if (f) ingestFile(f)
  }

  const submit = () => {
    const trimmed = body.trim()
    if (!trimmed) return
    onSave({
      id: 'n' + Date.now(),
      author: defaultAuthor || 'You',
      timestamp: ts,
      body: trimmed,
      attachmentDataUrl: attachment?.dataUrl || null,
      attachmentName: attachment?.name || null,
    })
  }

  return (
    <div
      className="fixed inset-0 z-[2000] bg-black/40 flex items-start justify-center overflow-y-auto py-8"
      onClick={onCancel}
    >
      <div className="bg-white w-full max-w-2xl rounded shadow-lg mx-4" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h2 className="text-base font-semibold text-gray-800">New Field Note</h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={submit}
              disabled={!body.trim()}
              className="px-3 py-1 text-sm bg-[#358ECD] text-white rounded hover:bg-[#2c7ab3] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Save Note
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="px-3 py-1 text-sm border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
        {/* Body */}
        <div className="px-5 py-4">
          <div className="border border-gray-200 rounded">
            <div className="px-3 pt-2 pb-1 text-[11px] text-gray-400 border-b border-gray-100">
              {formatFieldNoteTs(ts)}
            </div>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Enter note by typing…"
              rows={10}
              className="w-full px-3 py-2 text-sm focus:outline-none resize-none"
            />
          </div>

          {/* Attachment drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            className={`mt-3 border-2 border-dashed rounded p-6 text-center text-sm transition-colors ${
              dragOver ? 'border-[#358ECD] bg-[#358ECD]/5' : 'border-gray-200 bg-gray-50'
            }`}
          >
            {attachment ? (
              <div className="flex items-center justify-center gap-3">
                <img src={attachment.dataUrl} alt={attachment.name} className="h-16 w-16 object-cover rounded" />
                <span className="text-xs text-gray-600">{attachment.name}</span>
                <button
                  type="button"
                  onClick={() => setAttachment(null)}
                  className="text-xs text-red-500 hover:underline"
                >
                  Remove
                </button>
              </div>
            ) : (
              <span className="text-gray-400">
                Drag Attachments or{' '}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="text-[#358ECD] hover:underline"
                >
                  Upload Image
                </button>
              </span>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) ingestFile(f); e.target.value = '' }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * "All Field Notes" popup — search bar + New Note button in the header,
 * scrollable list of all notes (with attached images inline on the right
 * when present). Matches the XD viewAll layout.
 */
function FieldNotesViewAllModal({ open, notes, onClose, onNewNote, onDeleteNote }) {
  const [query, setQuery] = useState('')

  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return notes
    return notes.filter((n) =>
      (n.body || '').toLowerCase().includes(q) ||
      (n.author || '').toLowerCase().includes(q)
    )
  }, [notes, query])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-[2000] bg-black/40 flex items-start justify-center overflow-y-auto py-8"
      onClick={onClose}
    >
      <div className="bg-white w-full max-w-4xl rounded shadow-lg mx-4" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-800">All Field Notes</h2>
          <div className="flex items-center gap-3">
            <div className="relative">
              <svg className="absolute left-2.5 top-2 w-4 h-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search Notes"
                className="pl-9 pr-3 py-1.5 text-sm border border-gray-200 rounded w-64 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#358ECD]/30 focus:bg-white"
              />
            </div>
            <button
              type="button"
              onClick={onNewNote}
              className="px-3 py-1.5 text-sm bg-[#358ECD] text-white rounded hover:bg-[#2c7ab3]"
            >
              New Note
            </button>
            <button
              type="button"
              onClick={onClose}
              className="p-1 text-gray-500 hover:text-gray-700"
              aria-label="Close"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        </div>
        {/* Body */}
        <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="text-center text-sm text-gray-400 py-12">
              {query ? `No notes match "${query}".` : 'No field notes yet.'}
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map((n) => (
                <div key={n.id} className="bg-gray-50 rounded p-4 flex gap-4 group">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-gray-800">{n.author}</div>
                    <div className="text-[11px] text-gray-500 mb-1.5">{formatFieldNoteTs(n.timestamp)}</div>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">{n.body}</p>
                  </div>
                  {n.attachmentDataUrl && (
                    <img
                      src={n.attachmentDataUrl}
                      alt={n.attachmentName || 'attachment'}
                      className="w-32 h-24 object-cover rounded flex-shrink-0"
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => onDeleteNote(n.id)}
                    className="self-start text-[11px] text-gray-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                    title="Delete note"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Field Notes Widget — matches the XD spec:
 *
 *   • Header: "Field Notes" title, "New Note" + "View All" buttons on the right.
 *   • Body: compact list of recent notes — author (bold) and timestamp on
 *     the same line, body underneath, a paperclip icon for notes with
 *     attachments. Up to MAX_PREVIEW visible inline.
 *   • "New Note" opens the FieldNotesNewModal (textarea + image drop zone).
 *   • "View All" opens the FieldNotesViewAllModal (search + scrollable list
 *     with attachment thumbnails rendered inline).
 *
 * Notes persist to localStorage scoped per (project, widget instance) so
 * each Field Notes widget keeps its own list and projects don't bleed
 * into one another (same convention as the other configurable widgets in
 * this file). Backend persistence is a follow-up task.
 */
export function FieldNotesWidget({ widget }) {
  const { projectId } = useParams()
  const {
    isCustomizing = false,
    user,                               // not in context today, but referenced safely
  } = useDashboardContext()
  const lsKey = ['wq_fieldnotes', projectId, widget.id]

  const MAX_PREVIEW = 3

  const [notes, setNotes] = useState(() => {
    const v = lsGetScoped(lsKey[0], lsKey[1], lsKey[2], null)
    return Array.isArray(v) ? v : []
  })
  const [showNewModal, setShowNewModal] = useState(false)
  const [showViewAllModal, setShowViewAllModal] = useState(false)

  const persist = (next) => {
    setNotes(next)
    lsSetScoped(lsKey[0], lsKey[1], lsKey[2], next)
  }

  const addNote = (note) => {
    persist([note, ...notes])
    setShowNewModal(false)
  }
  const deleteNote = (id) => persist(notes.filter((n) => n.id !== id))

  const defaultAuthor = user?.name || user?.email || 'You'
  const previewNotes = notes.slice(0, MAX_PREVIEW)

  return (
    <XDCard
      title={widget.title || 'Field Notes'}
      subtitle={widget.description}
      headerRight={
        <div className="flex items-center gap-2">
          {!isCustomizing && (
            <button
              type="button"
              onClick={() => setShowNewModal(true)}
              className="text-white font-medium text-xs bg-[#358ECD] hover:bg-[#2d7ab8] py-1.5 px-3 rounded-lg transition"
            >
              New Note
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowViewAllModal(true)}
            disabled={isCustomizing}
            className="text-[#4D4D4D] flex items-center gap-2 font-medium text-xs bg-gray-200 hover:bg-gray-300 py-1.5 px-3 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
            title="Open all field notes"
          >
            <ExternalLinkIcon /> View All
          </button>
        </div>
      }
    >
      <div className="px-5 py-4">
        {previewNotes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center gap-1">
            <p className="text-gray-500 text-sm">No notes yet — click "New Note" to add one.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {previewNotes.map((n) => (
              <div key={n.id} className="border-b border-gray-100 pb-3 last:border-b-0">
                <div className="flex items-baseline justify-between mb-1">
                  <span className="text-sm font-semibold text-gray-800">{n.author}</span>
                  <span className="text-[11px] text-gray-500">{formatFieldNoteTs(n.timestamp)}</span>
                </div>
                <p className="text-xs text-gray-600 leading-relaxed flex items-start gap-1.5">
                  {n.attachmentDataUrl && (
                    <span className="text-gray-400 mt-0.5 flex-shrink-0" title={n.attachmentName || 'attachment'}>
                      <PaperclipIcon />
                    </span>
                  )}
                  <span className="line-clamp-2">{n.body}</span>
                </p>
              </div>
            ))}
            {notes.length > MAX_PREVIEW && (
              <button
                type="button"
                onClick={() => setShowViewAllModal(true)}
                className="block w-full text-[11px] text-[#358ECD] hover:underline text-center pt-1"
              >
                + {notes.length - MAX_PREVIEW} more — view all
              </button>
            )}
          </div>
        )}
      </div>

      <FieldNotesNewModal
        open={showNewModal}
        onSave={addNote}
        onCancel={() => setShowNewModal(false)}
        defaultAuthor={defaultAuthor}
      />
      <FieldNotesViewAllModal
        open={showViewAllModal}
        notes={notes}
        onClose={() => setShowViewAllModal(false)}
        onNewNote={() => { setShowViewAllModal(false); setShowNewModal(true) }}
        onDeleteNote={deleteNote}
      />
    </XDCard>
  )
}

/**
 * Custom HTML Widget
 */
export function CustomHTMLWidget({ widget, data, onConfigure }) {
  const { isCustomizing = false } = useDashboardContext()
  const html = data?.html || widget.rich_content || widget.data_config?.html || ''
  return (
    <XDCard
      title={widget.title || 'Custom HTML'}
      subtitle={widget.description}
      headerRight={
        !isCustomizing && onConfigure ? (
          <button
            onClick={() => onConfigure(widget)}
            className="p-1 text-gray-400 hover:text-[#358ECD] rounded transition"
            title="Edit HTML content"
          >
            <EditIcon />
          </button>
        ) : null
      }
    >
      <div className="p-6 overflow-auto">
        {html ? (
          <div
            className="prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        ) : (
          <p className="text-sm text-gray-400 italic text-center py-8">
            No content · click the ✎ icon to add HTML
          </p>
        )}
      </div>
    </XDCard>
  )
}

/**
 * Rich Content Widget — renders WYSIWYG HTML created in Django admin or frontend editor.
 * Content is stored in widget.rich_content (from MasterWidget.rich_content).
 * SECURITY: content originates from authenticated admin/staff users — treat as trusted.
 */
export function RichContentWidget({ widget }) {
  const html = widget.rich_content || widget.data_config?.rich_content || ''
  return (
    <div className="bg-white rounded-lg shadow-sm h-full flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 shrink-0">
        <div>
          <h3 className="font-bold text-[#4D4D4D] text-base">{widget.title}</h3>
          {widget.description && <p className="text-xs text-gray-400 mt-0.5 leading-tight">{widget.description}</p>}
        </div>
      </div>
      <div className="flex-1 overflow-auto p-4">
        {html ? (
          <div
            className="prose prose-sm max-w-none rich-content-render"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        ) : (
          <p className="text-sm text-gray-400 italic">No content yet. Click "Edit Content" in customize mode to add rich content.</p>
        )}
      </div>
    </div>
  )
}

/**
 * Help/Information Widget
 */
export function HelpWidget({ widget, data }) {
  return (
    <Card className="p-6 bg-blue-50 border border-blue-200">
      <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center gap-2">
        ❓ {widget.title}
      </h3>
      <div className="text-sm text-blue-800">
        <p className="mb-3">{data?.helpText || 'Help information not available'}</p>
        {data?.links && (
          <div className="space-y-2">
            {data.links.map((link, idx) => (
              <a key={idx} href={link.url} target="_blank" rel="noopener noreferrer" className="block text-blue-600 hover:underline">
                • {link.label}
              </a>
            ))}
          </div>
        )}
      </div>
    </Card>
  )
}

/**
 * Custom Widget - extensible for user-created widgets
 */
export function CustomWidget({ widget, data }) {
  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">{widget.title}</h3>
      <div className="bg-gray-50 rounded-lg p-4 text-gray-600 text-center">
        <p>Custom widget configuration</p>
        {data && <pre className="text-xs mt-2 overflow-auto">{JSON.stringify(data, null, 2)}</pre>}
      </div>
    </Card>
  )
}

/**
 * Widget renderer - dynamically loads the appropriate widget component
 */
export function WidgetRenderer({ widget, data, isEditing = false, onEdit, onDelete, noWrap = false, onConfigure }) {
  const widgetComponents = {
    // Basic
    summary_card: SummaryCard,
    live_data: LiveDataWidget,
    site_conditions: SiteConditionsWidget,
    data_table: DataTableWidget,
    
    // Charts and Graphs
    line_chart: LineChartWidget,
    smooth_line_chart: SmoothCurveLineChartWidget,
    combo_chart: ComboChartWidget,
    multi_chart: MultiChartWidget,
    area_chart: AreaChartWidget,
    column_chart: ColumnChartWidget,
    stacked_column_chart: StackedColumnChartWidget,
    bar_chart: BarChartWidget,
    pie_chart: PieChartWidget,
    doughnut_chart: DoughnutChartWidget,
    pie_chart_3d: Pie3DChartWidget,
    
    // Gauges and Dials
    wind_rose: WindRoseWidget,
    gauge_series: GaugeSeriesWidget,
    bullet_graph: BulletGraphWidget,
    solid_gauge: SolidGaugeWidget,
    
    // Weather Panels
    air_temperature: AirTemperatureWidget,
    barometric_pressure: BarometricPressureWidget,
    daily_rain: DailyRainWidget,
    heading: HeadingWidget,
    humidity: HumidityWidget,
    interval_rain: IntervalRainWidget,
    lightning: LightningWidget,
    precipitation: PrecipitationWidget,
    pressure: PressureWidget,
    rain_intensity: RainIntensityWidget,
    solar_radiation: SolarRadiationWidget,
    total_rain: TotalRainWidget,
    wind: WindWidget,
    
    // Water Panels
    ammonium: AmmoniumWidget,
    bga: BGAWidget,
    chlorophyll: ChlorophyllWidget,
    conductivity: ConductivityWidget,
    dissolved_oxygen: DissolvedOxygenWidget,
    hydrocarbons: HydrocarbonsWidget,
    level: LevelWidget,
    oil_detection: OilDetectionWidget,
    orp: ORPWidget,
    ph: PHWidget,
    pressure_water: WaterPressureWidget,
    salinity: SalinityWidget,
    snow: SnowWidget,
    sp_conductance: SpConductanceWidget,
    temperature: WaterTemperatureWidget,
    turbidity: TurbidityWidget,
    water_current: WaterCurrentWidget,
    wave: WaveWidget,
    
    // Soil Panels
    soil_conductivity: SoilConductivityWidget,
    soil_moisture: SoilMoistureWidget,
    soil_salinity: SoilSalinityWidget,
    soil_temperature: SoilTemperatureWidget,
    
    // Other
    gps: GPSWidget,
    
    // Alarms
    alarm_summary: AlarmSummaryWidget,
    individual_alarm: IndividualAlarmWidget,
    
    // Miscellaneous
    project_description: ProjectDescriptionWidget,
    project_photo: ProjectPhotoWidget,
    gallery: GalleryWidget,
    webcam: WebcamWidget,
    field_notes: FieldNotesWidget,
    custom_html: CustomHTMLWidget,
    rich_content: RichContentWidget,
    help: HelpWidget,
    
    // Video and Map
    video_feed: VideoFeedWidget,
    map_view: MapViewWidget,
    performance_metric: PerformanceMetricWidget,
    custom: CustomWidget,

    // New XD-design widgets
    environmental_news: EnvironmentalNewsWidget,
    event_monitor: EventMonitorWidget,
    support_project: SupportProjectWidget,
    donation: SupportProjectWidget,
    disclaimer: DisclaimerWidget,
    knowledge_base: KnowledgeBaseWidget,
  }

  const Component = widgetComponents[widget.widget_type] || CustomWidget

  if (noWrap) {
    return <Component widget={widget} data={data} onConfigure={onConfigure} />
  }

  return (
    <div
      style={{
        gridRow: `span ${widget.size_rows || 1}`,
        gridColumn: `span ${widget.size_cols || 1}`,
      }}
      className="relative group h-full"
    >
      {isEditing && (
        <div className="absolute top-2 right-2 z-10 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          {onEdit && (
            <button
              onClick={() => onEdit(widget)}
              className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            >
              ✎
            </button>
          )}
          {onDelete && (
            <button
              onClick={() => onDelete(widget.id)}
              className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
            >
              ✕
            </button>
          )}
        </div>
      )}
      <Component widget={widget} data={data} />
    </div>
  )
}
